import React, { useState, useEffect, useRef, useCallback } from 'react';
import api from '../services/api';
import { isVisualizacao } from '../utils/permissoes';
import { useAuth } from '../context/AuthContext';
import PainelContas from '../components/PainelContas';

const fmt = v => 'R$ ' + parseFloat(v || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 });
const fmtData = d => { if (!d) return '—'; const p = String(d).split('T')[0].split('-'); return p.length === 3 ? `${p[2]}/${p[1]}/${p[0]}` : '—'; };
const toISO = d => d ? d.toISOString().split('T')[0] : '';
const hoje = toISO(new Date());
const inicioMes = toISO(new Date(new Date().getFullYear(), new Date().getMonth(), 1));
const fimMes = toISO(new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0));

// Cor do badge de categoria: a cor escolhida pelo usuário manda. Categoria na cor padrão
// (cinza #6B7A8D) ganha cor automática pelo nome — só visual, nada é gravado no banco.
const COR_CATEGORIA_PADRAO = '#6B7A8D';
const CORES_POR_NOME = [
  [['comiss'], '#2D7D4F'],
  [['salari', 'pro labore', 'fgts'], '#1A6DFF'],
  [['anunci', 'marketing'], '#F39C12'],
  [['compra de veic', 'compra de duact'], '#E84393'],
  [['emprestimo', 'financiamento'], '#7C5CFF'],
  [['manutenc', 'peca', 'pintura', 'revisao', 'lavagem', 'capacete'], '#B7791F'],
  [['imposto', 'iss', 'darf', 'simples', 'sefaz', 'ipva', 'detran', 'licenciamento', 'multa'], '#E74C3C'],
  [['sistema', 'internet', 'energia', 'agua', 'aluguel', 'contabilidade', 'seguranca', 'chip'], '#0EA5A4'],
  [['venda', 'rentabilidade', 'reembolso', 'receita'], '#10B981'],
];
const PALETA_CATEGORIA = ['#1A6DFF', '#F39C12', '#E84393', '#7C5CFF', '#0EA5A4', '#B7791F', '#2D7D4F', '#E74C3C'];
const normCat = (t) => (t || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
function corCategoria(nome, cor) {
  if (cor && cor.toUpperCase() !== COR_CATEGORIA_PADRAO) return cor;
  const n = normCat(nome);
  const achou = CORES_POR_NOME.find(([chaves]) => chaves.some(k => n.includes(k)));
  if (achou) return achou[1];
  let h = 0; for (const ch of n) h = (h * 31 + ch.charCodeAt(0)) >>> 0;
  return PALETA_CATEGORIA[h % PALETA_CATEGORIA.length];
}

const card = { background: '#fff', border: '0.5px solid rgba(26,58,92,0.12)', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.08)', padding: '18px 20px' };
const th = { padding: '10px 12px', background: '#F5F7FA', color: '#9AA3AE', fontSize: '11px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.4px', textAlign: 'left', borderBottom: '0.5px solid rgba(26,58,92,0.1)' };
// Cards de saldo por banco (topo do Dashboard, A Pagar e A Receber): mesmo visual dos KPIs
const kpiBanco = { background: '#1A2332', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.08)', padding: '18px 20px', color: '#fff', minWidth: 0 };
const kpiBancoLbl = { fontSize: '11px', color: 'rgba(255,255,255,0.6)', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.3px', marginBottom: '8px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' };
const kpiBancoVal = (v) => ({ fontSize: '20px', fontWeight: '700', fontFamily: "'DM Mono',monospace", whiteSpace: 'nowrap', color: parseFloat(v) >= 0 ? '#fff' : '#FF8080' });
const td = { padding: '12px 12px', borderBottom: '0.5px solid rgba(26,58,92,0.07)', fontSize: '13px', color: '#1C2533', verticalAlign: 'middle' };
const inpStyle = { width: '100%', padding: '8px 10px', background: '#F4F6F9', border: '0.5px solid rgba(26,58,92,0.15)', borderRadius: '7px', color: '#1C2533', fontSize: '13px', fontFamily: "'DM Sans',system-ui,sans-serif", boxSizing: 'border-box' };
const lbl = { color: '#6B7A8D', fontSize: '11px', fontWeight: '600', display: 'block', marginBottom: '5px', textTransform: 'uppercase', letterSpacing: '0.3px' };
const ABAS = ['Dashboard', 'A Pagar', 'A Receber', 'Fornecedores', 'Lançamentos', 'Bancos', 'Importar OFX', 'Categorias'];

export default function Financeiro() {
  const { usuario } = useAuth();
  const isAdmin = usuario?.role === 'admin' || usuario?.nivel === 'admin';
  const somenteLeitura = isVisualizacao(usuario); // só vê: sem lançar/pagar/editar/importar
  const abas = somenteLeitura ? ABAS.filter(a => a !== 'Importar OFX') : ABAS;
  const [aba, setAba] = useState('Dashboard');
  const [dashboard, setDashboard] = useState(null);
  // Painel Recebimentos/Pagamentos: status 'carregando' | 'ok' | 'off' (403 = não liberado → blocos antigos) | 'erro'
  const [painel, setPainel] = useState({ status: 'carregando', dados: null, erro: null });
  const [lancamentos, setLancamentos] = useState([]);
  const [contas, setContas] = useState([]);
  const [categorias, setCategorias] = useState([]);
  const [fornecedores, setFornecedores] = useState([]);
  const [modalLanc, setModalLanc] = useState(false);
  const [modalPagar, setModalPagar] = useState(null);
  const [modalConta, setModalConta] = useState(false);
  const [form, setForm] = useState({});
  const [formConta, setFormConta] = useState({});
  const [filtro, setFiltro] = useState({ inicio: inicioMes, fim: fimMes, status: '', conta_id: '', tipo: '' });
  const [busca, setBusca] = useState('');
  const [selecionados, setSelecionados] = useState([]);
  const [loading, setLoading] = useState(false);
  const [ofxTransacoes, setOfxTransacoes] = useState([]);
  const [ofxContaId, setOfxContaId] = useState('');
  const [ofxLoading, setOfxLoading] = useState(false);
  const [vendas, setVendas] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [buscaCliente, setBuscaCliente] = useState({});
  const [modoLinha, setModoLinha] = useState({}); // {idx: 'manual'|'venda'|null}
  const [dadosLinha, setDadosLinha] = useState({}); // {idx: {categoria_id, fornecedor_id, descricao, venda_id}}
  const [novaCategoria, setNovaCategoria] = useState({ show: false, nome: '', tipo: 'despesa', cor: '#6B7A8D' });
  const [novoFornecedor, setNovoFornecedor] = useState({ show: false, nome: '', cpf_cnpj: '' });
  const [modalDividir, setModalDividir] = useState(null);
  const [modalParcial, setModalParcial] = useState(null);
  const [titulo, setTitulo] = useState(null); // detalhe "título + baixas" (Nibo) — GET /lancamentos/:id/titulo
  const [tituloValor, setTituloValor] = useState('');
  const [tituloPag, setTituloPag] = useState(null); // mini-form Registrar pagamento { valor, data_pagamento, conta_id }
  const [confirmExcluirFilho, setConfirmExcluirFilho] = useState(null); // lançamento filho aguardando confirmação de exclusão
  const [modalCategoria, setModalCategoria] = useState(null);
  const [formCategoria, setFormCategoria] = useState({});
  const [valorParcial, setValorParcial] = useState('');
  const [modalExtrato, setModalExtrato] = useState(null);
  const [extrato, setExtrato] = useState(null);
  const [filtroExtrato, setFiltroExtrato] = useState({ inicio: inicioMes, fim: fimMes });
  const [refreshingExtrato, setRefreshingExtrato] = useState(false);
  const [modalTransf, setModalTransf] = useState(false);
  const [formTransf, setFormTransf] = useState({ conta_origem: '', conta_destino: '', valor: '', data: '', descricao: 'Transferência entre contas' }); // lançamento a dividir
  const [divisoes, setDivisoes] = useState([{ forma: '', valor: '', conta_id: '' }]);
  const ofxRef = useRef();
  const descRef = useRef();

  // FORNECEDORES (visão financeira)
  const [fornResumo, setFornResumo] = useState([]);
  const [fornSel, setFornSel] = useState(null); // fornecedor selecionado (objeto do resumo)
  const [fornDetalhe, setFornDetalhe] = useState(null); // { proximas, pagas }
  const [fornLoading, setFornLoading] = useState(false);
  const [buscaForn, setBuscaForn] = useState('');
  const [selLanc, setSelLanc] = useState([]); // ids de lançamentos selecionados no painel
  const [editLanc, setEditLanc] = useState(null); // lançamento em edição inline
  const [editForm, setEditForm] = useState({});
  const [modalVencLote, setModalVencLote] = useState(false);
  const [novaVencLote, setNovaVencLote] = useState(hoje);
  const [modalValorLote, setModalValorLote] = useState(false);
  const [novoValorLote, setNovoValorLote] = useState('');
  const [modalPagarLote, setModalPagarLote] = useState(false);
  const [pagLote, setPagLote] = useState({ data_pagamento: hoje, conta_id: '' });

  const carregarFornecedoresResumo = useCallback((f) => {
    const ff = f || { inicio: inicioMes, fim: fimMes };
    return api.get('/financeiro/fornecedores', { params: { inicio: ff.inicio, fim: ff.fim } })
      .then(r => { setFornResumo(r.data); return r.data; }).catch(console.error);
  }, []);

  const abrirFornecedor = async (forn) => {
    setFornSel(forn);
    setFornDetalhe(null);
    setSelLanc([]);
    setFornLoading(true);
    try {
      const r = await api.get('/financeiro/fornecedores/' + forn.id + '/lancamentos');
      setFornDetalhe(r.data);
    } catch (e) { console.error(e); }
    finally { setFornLoading(false); }
  };

  const recarregarFornecedor = async () => {
    if (!fornSel) return;
    const r = await api.get('/financeiro/fornecedores/' + fornSel.id + '/lancamentos');
    setFornDetalhe(r.data);
    // Atualiza também os cards do painel (Em aberto / Vencido / Pago no período)
    const lista = await carregarFornecedoresResumo();
    const atualizado = lista && lista.find(f => f.id === fornSel.id);
    if (atualizado) setFornSel(atualizado);
  };

  const salvarEdicaoLanc = async () => {
    try {
      await api.put('/financeiro/lancamentos/' + editLanc.id + '/editar', {
        data_vencimento: editForm.data_vencimento,
        valor: editForm.valor,
        descricao: editForm.descricao,
      });
      setEditLanc(null); setEditForm({});
      await recarregarFornecedor();
      carregarDashboard();
    } catch (err) { alert('Erro: ' + (err.response?.data?.error || err.message)); }
  };

  // ---------- Título + baixas (Nibo) ----------
  const abrirTitulo = async (id, comPagamento = false) => {
    try {
      const r = await api.get('/financeiro/lancamentos/' + id + '/titulo');
      setTitulo(r.data); setTituloValor(String(r.data.valor_total));
      setTituloPag(comPagamento && r.data.em_aberto > 0 ? { valor: String(r.data.em_aberto), data_pagamento: hoje, conta_id: r.data.conta_id ? String(r.data.conta_id) : '' } : null);
    } catch (err) { alert('Erro: ' + (err.response?.data?.error || err.message)); }
  };
  const atualizarAposTitulo = async (id) => {
    try {
      const r = await api.get('/financeiro/lancamentos/' + id + '/titulo');
      setTitulo(r.data); setTituloValor(String(r.data.valor_total));
    } catch { setTitulo(null); }
    recarregarLancamentos(); carregarDashboard();
    if (abaRef.current === 'Fornecedores') recarregarFornecedor();
  };
  const salvarValorTotal = async () => {
    const v = parseFloat(String(tituloValor).replace(',', '.'));
    if (!(v > 0)) { alert('Valor inválido'); return; }
    if (v < titulo.pago - 0.005) { alert('O valor total não pode ser menor que o já pago (' + fmt(titulo.pago) + ')'); return; }
    try {
      await api.put('/financeiro/lancamentos/' + titulo.titulo_id + '/editar', { valor: v });
      await atualizarAposTitulo(titulo.titulo_id);
    } catch (err) { alert(err.response?.data?.error || 'Erro'); }
  };
  const registrarPagamentoTitulo = async () => {
    const v = parseFloat(String(tituloPag.valor).replace(',', '.'));
    if (!(v > 0)) { alert('Valor inválido'); return; }
    if (v > titulo.em_aberto + 0.005) { alert('Valor maior que o em aberto (' + fmt(titulo.em_aberto) + ')'); return; }
    try {
      await api.post('/financeiro/lancamentos/' + titulo.titulo_id + '/baixa', { valor: v, data_pagamento: tituloPag.data_pagamento || hoje, conta_id: tituloPag.conta_id || null });
      setTituloPag(null);
      await atualizarAposTitulo(titulo.titulo_id);
    } catch (err) { alert(err.response?.data?.error || 'Erro'); }
  };
  const cancelarBaixa = async (b) => {
    if (!window.confirm('Cancelar este pagamento de ' + fmt(b.valor) + '? O valor voltará para Em aberto.')) return;
    try {
      await api.post('/financeiro/lancamentos/' + b.id + '/estornar');
      await atualizarAposTitulo(titulo.titulo_id);
    } catch (err) { alert(err.response?.data?.error || 'Erro'); }
  };

  // Filho de fracionamento (pai status='fracionado'): pede confirmação no modal,
  // pois o backend recria o saldo como pendente. Demais: confirm simples de sempre.
  const excluirLancFornecedor = async (l, confirmado = false) => {
    const filhoFracionado = !!l.fracionado; // título com pagamento parcial (lista agrupada)
    if (filhoFracionado && !confirmado) { setConfirmExcluirFilho(l); return; }
    if (!filhoFracionado && !window.confirm('Excluir este lançamento? Esta ação não pode ser desfeita.')) return;
    try {
      const alvos = filhoFracionado ? (l.pendente_ids || []) : [l.id];
      if (!alvos.length) { alert('Não há parcela pendente para excluir neste título. Ajuste o valor total no detalhe.'); return; }
      for (const id of alvos) await api.delete('/financeiro/lancamentos/' + id + '/excluir');
      setSelLanc(s => s.filter(x => x !== l.id));
      if (filhoFracionado) { await recarregarFornecedor(); carregarDashboard(); return; } // saldo recriado c/ novo id
      // Remove o item da lista localmente (sem recarregar todo o detalhe)
      setFornDetalhe(d => d ? { ...d, proximas: d.proximas.filter(x => x.id !== l.id) } : d);
      // Atualiza os totais do fornecedor (resumo lateral + lista) e o dashboard
      const r = await api.get('/financeiro/fornecedores', { params: { inicio: inicioMes, fim: fimMes } });
      setFornResumo(r.data);
      const atualizado = fornSel && r.data.find(f => f.id === fornSel.id);
      if (atualizado) setFornSel(atualizado);
      carregarDashboard();
    } catch (err) { alert('Erro: ' + (err.response?.data?.error || err.message)); }
  };

  const salvarVencimentoLote = async () => {
    if (!selLanc.length) { alert('Nenhum lançamento selecionado'); return; }
    if (!novaVencLote) { alert('Informe a nova data'); return; }
    try {
      await api.put('/financeiro/lancamentos/alterar-vencimento-lote', { ids: selLanc, data_vencimento: novaVencLote });
      setModalVencLote(false); setSelLanc([]);
      await recarregarFornecedor();
      carregarDashboard();
    } catch (err) { alert('Erro: ' + (err.response?.data?.error || err.message)); }
  };

  const salvarValorLote = async () => {
    if (!selLanc.length) { alert('Nenhum lançamento selecionado'); return; }
    const v = parseFloat(String(novoValorLote).replace(',', '.'));
    if (!(v > 0)) { alert('Informe um valor válido'); return; }
    if (!window.confirm(`Alterar o valor de ${selLanc.length} lançamento(s) para ${fmt(v)}?`)) return;
    try {
      const r = await api.put('/financeiro/lancamentos/alterar-valor-lote', { ids: selLanc, valor: v });
      setModalValorLote(false); setNovoValorLote(''); setSelLanc([]);
      if (r.data.ignorados > 0) alert(`${r.data.alterados} alterado(s). ${r.data.ignorados} ignorado(s) por não estarem pendentes.`);
      await recarregarFornecedor();
      carregarDashboard();
    } catch (err) { alert('Erro: ' + (err.response?.data?.error || err.message)); }
  };

  const pagarLoteFornecedor = async () => {
    if (!selLanc.length) { alert('Nenhum lançamento selecionado'); return; }
    if (!pagLote.data_pagamento) { alert('Informe a data de pagamento'); return; }
    try {
      const r = await api.put('/financeiro/lancamentos/pagar-lote', { ids: selLanc, data_pagamento: pagLote.data_pagamento, conta_id: pagLote.conta_id || null });
      setModalPagarLote(false); setSelLanc([]);
      if (r.data.ignorados > 0) alert(`${r.data.pagos} pago(s). ${r.data.ignorados} ignorado(s) por não estarem pendentes.`);
      await recarregarFornecedor();
      carregarDashboard();
    } catch (err) { alert('Erro: ' + (err.response?.data?.error || err.message)); }
  };

  const agendarPagamentoFornecedor = () => {
    setForm({ tipo: 'despesa', total_parcelas: 1, data_vencimento: hoje, modo_lancamento: 'simples', fornecedor_id: String(fornSel.id) });
    setModalLanc(true);
  };

  const excluirLoteFornecedor = async () => {
    if (!selLanc.length) { alert('Nenhum lançamento selecionado'); return; }
    if (!window.confirm(`Excluir ${selLanc.length} lançamento(s)? Esta ação não pode ser desfeita.`)) return;
    try {
      const r = await api.post('/financeiro/lancamentos/excluir-lote', { ids: selLanc });
      const ign = r.data.ignorados || [];
      setSelLanc([]);
      if (ign.length) alert(`${r.data.excluidos} excluído(s). ${ign.length} pulado(s): ${ign.map(i => '#' + i.id + ' (' + i.motivo + ')').join(', ')}`);
      await recarregarFornecedor();
      carregarDashboard();
    } catch (err) { alert('Erro: ' + (err.response?.data?.error || err.message)); }
  };

  const carregarExtrato = async (conta, f) => {
    setRefreshingExtrato(true);
    try {
      const ff = f || filtroExtrato;
      const r = await api.get("/financeiro/contas/" + conta.id + "/extrato", { params: ff });
      setExtrato(r.data);
    } catch(e) { console.error(e); }
    finally { setRefreshingExtrato(false); }
  };

  const abrirExtrato = async (conta) => {
    setModalExtrato(conta);
    const ff = filtroExtrato;
    const r = await api.get("/financeiro/contas/" + conta.id + "/extrato", { params: ff });
    setExtrato(r.data);
  };
  const setV = (k, v) => setForm(f => ({ ...f, [k]: v }));

  // Mês exibido na aba "Mês atual" do painel (null = mês vigente). Ref: recarregar após Baixar/editar mantém o mês.
  const painelMesRef = useRef(null);
  const [painelCarregando, setPainelCarregando] = useState(false);
  const carregarPainel = useCallback(() => { setPainelCarregando(true); return api.get('/financeiro/painel-contas', { params: painelMesRef.current ? { mes: painelMesRef.current } : {} })
    .then(r => setPainel({ status: 'ok', dados: r.data, erro: null }))
    .catch(err => {
      if (err.response?.status === 403) setPainel({ status: 'off', dados: null, erro: null });
      else setPainel(p => ({ status: 'erro', dados: p.dados, erro: err.response?.data?.error || err.response?.data?.erro || err.message || 'Erro ao carregar painel' }));
    }).finally(() => setPainelCarregando(false)); }, []);
  const mudarMesPainel = (mes) => { painelMesRef.current = mes; carregarPainel(); };
  const carregarDashboard = useCallback(() => {
    carregarPainel();
    return api.get('/financeiro/dashboard').then(r => setDashboard(r.data)).catch(console.error);
  }, [carregarPainel]);

  // Ações do painel: reutilizam os fluxos existentes (modal Pagar e modal Editar lançamento)
  // Mini-form "Pagar" (02/11): valor (= em aberto), data (= hoje) e conta (= do lançamento), editáveis.
  // Valor menor que o em aberto = baixa parcial (POST /baixa, mesma regra do detalhe do título).
  const abrirPagar = (l) => {
    setModalPagar(l);
    setForm({ valor_pag: String(l.em_aberto ?? l.valor ?? ''), data_pagamento: hoje, conta_id_pag: l.conta_id ? String(l.conta_id) : '' });
  };
  const baixarDoPainel = (l) => abrirPagar(l);
  const abrirDoPainel = (l) => {
    setForm({ ...l, data_vencimento: l.vencimento, conta_id: l.conta_id ? String(l.conta_id) : '', categoria_id: l.categoria_id ? String(l.categoria_id) : '', fornecedor_id: l.fornecedor_id ? String(l.fornecedor_id) : '' });
    setModalLanc(true);
  };
  const verTodosDoPainel = (tipo, abaPainel) => {
    const base = painel.dados?.hoje || hoje; // "hoje" do servidor (America/Fortaleza)
    const somaDias = (iso, n) => { const d = new Date(iso + 'T12:00:00Z'); d.setUTCDate(d.getUTCDate() + n); return d.toISOString().split('T')[0]; };
    const per = painel.dados?.periodo;
    const datas = abaPainel === 'proximos' ? { inicio: base, fim: somaDias(base, 30) }
      : abaPainel === 'vencido' ? { inicio: '', fim: somaDias(base, -1) }
      : abaPainel === 'mes_atual' && per ? { inicio: per.de, fim: per.ate }
      : { inicio: '', fim: '' }; // em_aberto = todo pendente
    setFiltro(f => ({ ...f, ...datas, status: 'pendente', tipo }));
    setAba(tipo === 'receita' ? 'A Receber' : 'A Pagar');
  };
  const carregarContas = useCallback(() => api.get('/financeiro/contas').then(r => setContas(r.data)).catch(console.error), []);
  const carregarCategorias = useCallback(() => api.get('/financeiro/categorias').then(r => setCategorias(r.data)).catch(console.error), []);

  // Refs mantêm sempre o filtro/aba atuais, para que ações (editar, pagar, dividir,
  // excluir) recarreguem a lista respeitando os filtros aplicados pelo usuário.
  const filtroRef = useRef(filtro);
  const abaRef = useRef(aba);
  useEffect(() => { filtroRef.current = filtro; }, [filtro]);
  useEffect(() => { abaRef.current = aba; }, [aba]);

  const carregarLancamentos = useCallback((f) => {
    const ff = f || filtroRef.current;
    const params = {};
    if (ff.inicio) params.inicio = ff.inicio;
    if (ff.fim) params.fim = ff.fim;
    if (ff.status) params.status = ff.status;
    if (ff.conta_id) params.conta_id = ff.conta_id;
    if (ff.tipo) params.tipo = ff.tipo;
    if (abaRef.current === 'A Pagar' || abaRef.current === 'A Receber') params.agrupar = '1'; // fracionamento = 1 linha (título)
    api.get('/financeiro/lancamentos', { params }).then(r => setLancamentos(r.data)).catch(console.error);
  }, []); // eslint-disable-line

  // Recarrega a lista de lançamentos preservando os filtros atuais (data, status,
  // conta) e derivando o tipo da aba ativa (A Pagar=despesa, A Receber=receita).
  const recarregarLancamentos = useCallback(() => {
    const a = abaRef.current;
    const tipoAba = a === 'A Pagar' ? 'despesa' : a === 'A Receber' ? 'receita' : '';
    carregarLancamentos({ ...filtroRef.current, tipo: tipoAba || filtroRef.current.tipo });
  }, [carregarLancamentos]);

  useEffect(() => {
    carregarDashboard();
    carregarContas();
    carregarCategorias();
    carregarLancamentos();
    if (!somenteLeitura) { // só alimentam os formulários de lançamento
      api.get('/fornecedores').then(r => setFornecedores(r.data)).catch(console.error);
      api.get('/vendas').then(r => setVendas(r.data)).catch(console.error);
      api.get('/clientes').then(r => setClientes(r.data)).catch(console.error);
    }
  }, []); // eslint-disable-line

  useEffect(() => {
    setBusca('');
    setSelecionados([]);
    if (aba === 'A Pagar') carregarLancamentos({ ...filtro, tipo: 'despesa' });
    else if (aba === 'A Receber') carregarLancamentos({ ...filtro, tipo: 'receita' });
    else if (aba === 'Lançamentos') carregarLancamentos(filtro);
    else if (aba === 'Fornecedores') { setFornSel(null); setFornDetalhe(null); setBuscaForn(''); carregarFornecedoresResumo(); }
  }, [aba]); // eslint-disable-line

  const salvarLancamento = async (e, continuar = false) => {
    if (e && e.preventDefault) e.preventDefault();
    setLoading(true);
    try {
      const dadosEnvio = { ...form };
      delete dadosEnvio._pagManual;
      if (form.modo_lancamento === 'fixo') {
        dadosEnvio.valor = (parseFloat(form.valor_parcela || 0) * parseInt(form.total_parcelas || 1)).toFixed(2);
      }
      if (form.id) await api.put('/financeiro/lancamentos/' + form.id, dadosEnvio);
      else await api.post('/financeiro/lancamentos', dadosEnvio);
      recarregarLancamentos(); carregarDashboard();
      if (abaRef.current === 'Fornecedores') recarregarFornecedor();
      if (continuar) {
        // Mantém o modal aberto e limpa o formulário para o próximo lançamento
        setForm({});
        setTimeout(() => descRef.current && descRef.current.focus(), 0);
      } else {
        setModalLanc(false); setForm({});
      }
    } catch (err) { alert(err.response?.data?.error || 'Erro'); }
    finally { setLoading(false); }
  };

  const pagarLancamento = async () => {
    const aberto = parseFloat(modalPagar.em_aberto ?? modalPagar.valor ?? 0);
    const v = parseFloat(String(form.valor_pag ?? '').replace(',', '.'));
    if (!(v > 0)) { alert('Informe o valor do pagamento'); return; }
    if (v > aberto + 0.005) { alert('Valor maior que o em aberto (' + fmt(aberto) + ')'); return; }
    try {
      await api.post('/financeiro/lancamentos/' + modalPagar.id + '/baixa', { valor: v, data_pagamento: form.data_pagamento || hoje, conta_id: form.conta_id_pag || null });
      setModalPagar(null); setForm({});
      if (titulo) atualizarAposTitulo(titulo.titulo_id);
      recarregarLancamentos(); carregarDashboard();
      if (abaRef.current === 'Fornecedores') recarregarFornecedor();
    } catch (err) { alert(err.response?.data?.error || 'Erro'); }
  };

  const pagarParcial = async () => {
    try {
      const v = parseFloat(valorParcial);
      if (!v || v <= 0) { alert('Informe o valor recebido'); return; }
      if (v > parseFloat(modalParcial.valor)) { alert('Valor maior que o lançamento'); return; }
      await api.put('/financeiro/lancamentos/' + modalParcial.id + '/pagar-parcial', {
        valor_recebido: v,
        data_pagamento: form.data_pagamento || hoje,
        conta_id: form.conta_id_pag || modalParcial.conta_id || null
      });
      setModalParcial(null); setValorParcial(''); setForm({});
      recarregarLancamentos(); carregarDashboard();
    } catch (err) { alert(err.response?.data?.error || 'Erro'); }
  };

  const cancelarUm = async (id) => {
    if (!window.confirm('Cancelar este lançamento?')) return;
    await api.delete('/financeiro/lancamentos/' + id);
    recarregarLancamentos();
    carregarDashboard();
  };

  const cancelarLote = async () => {
    if (!selecionados.length) { alert('Nenhum selecionado'); return; }
    if (!window.confirm(`Cancelar ${selecionados.length} lançamento(s)?`)) return;
    try {
      await api.post('/financeiro/lancamentos/cancelar-lote', { ids: selecionados });
      setSelecionados([]);
      // Mantém os filtros atuais (data/status/conta/tipo) após o cancelamento em lote
      recarregarLancamentos();
      carregarDashboard();
    } catch (err) { alert('Erro: ' + (err.response?.data?.error || err.message)); }
  };

  const salvarConta = async (e) => {
    e.preventDefault();
    try {
      if (formConta.id) await api.put('/financeiro/contas/' + formConta.id, formConta);
      else await api.post('/financeiro/contas', formConta);
      setModalConta(false); setFormConta({});
      carregarContas(); carregarDashboard();
    } catch (err) { alert(err.response?.data?.error || 'Erro'); }
  };

  const salvarNovoFornecedor = async () => {
    if (!novoFornecedor.nome) { alert('Digite o nome'); return; }
    try {
      const r = await api.post('/fornecedores', { nome: novoFornecedor.nome, cpf_cnpj: novoFornecedor.cpf_cnpj });
      await api.get('/fornecedores').then(res => setFornecedores(res.data));
      setV('fornecedor_id', String(r.data.id));
      setNovoFornecedor({ show: false, nome: '', cpf_cnpj: '' });
    } catch(e) { alert('Erro ao criar fornecedor'); }
  };

  const salvarNovaCategoria = async () => {
    if (!novaCategoria.nome) { alert('Digite o nome'); return; }
    try {
      const r = await api.post('/financeiro/categorias', { nome: novaCategoria.nome, tipo: novaCategoria.tipo, cor: novaCategoria.cor });
      await carregarCategorias();
      setV('categoria_id', String(r.data.id));
      setNovaCategoria({ show: false, nome: '', tipo: 'despesa', cor: '#6B7A8D' });
    } catch (err) { alert('Erro'); }
  };

  const salvarTransferencia = async () => {
    const { conta_origem, conta_destino, valor, data, descricao } = formTransf;
    if (!conta_origem || !conta_destino || !valor || !data) { alert('Preencha todos os campos'); return; }
    if (conta_origem === conta_destino) { alert('Origem e destino não podem ser iguais'); return; }
    try {
      const dataHoje = data || new Date().toISOString().split('T')[0];
      // Saída da conta origem
      await api.post('/financeiro/lancamentos', {
        tipo: 'despesa', descricao: descricao + ' (saída)',
        valor, data_vencimento: dataHoje, data_pagamento: dataHoje,
        status: 'pago', conta_id: conta_origem, total_parcelas: 1, modo_lancamento: 'simples',
        transferencia: true
      });
      // Entrada na conta destino
      await api.post('/financeiro/lancamentos', {
        tipo: 'receita', descricao: descricao + ' (entrada)',
        valor, data_vencimento: dataHoje, data_pagamento: dataHoje,
        status: 'pago', conta_id: conta_destino, total_parcelas: 1, modo_lancamento: 'simples',
        transferencia: true
      });
      setModalTransf(false);
      setFormTransf({ conta_origem: '', conta_destino: '', valor: '', data: '', descricao: 'Transferência entre contas' });
      recarregarLancamentos(); carregarDashboard();
      alert('Transferência registrada com sucesso!');
    } catch (err) { alert('Erro: ' + (err.response?.data?.error || err.message)); }
  };

  const abrirDividir = (l) => {
    // Parsear formas existentes da descrição
    const formas = l.descricao ? l.descricao.split('+').map(f => {
      const match = f.trim().match(/^(.+?)\s*\(R\$\s*([\d.,]+)\)$/);
      if (match) return { forma: match[1].trim(), valor: match[2].replace('.','').replace(',','.'), conta_id: l.conta_id ? String(l.conta_id) : '' };
      return { forma: f.trim(), valor: '', conta_id: l.conta_id ? String(l.conta_id) : '' };
    }) : [{ forma: '', valor: '', conta_id: '' }];
    setDivisoes(formas.length > 1 ? formas : [{ forma: '', valor: '', conta_id: '' }, { forma: '', valor: '', conta_id: '' }]);
    setModalDividir(l);
  };

  const salvarDivisao = async () => {
    const validas = divisoes.filter(d => d.forma && d.valor);
    if (validas.length < 2) { alert('Adicione pelo menos 2 formas de pagamento com valores'); return; }
    try {
      // Cancelar lançamento original
      await api.delete('/financeiro/lancamentos/' + modalDividir.id);
      // Criar um lançamento para cada forma
      for (const d of validas) {
        await api.post('/financeiro/lancamentos', {
          tipo: modalDividir.tipo,
          descricao: modalDividir.descricao + ' — ' + d.forma,
          valor: d.valor,
          data_vencimento: modalDividir.data_vencimento,
          data_pagamento: modalDividir.data_pagamento,
          status: modalDividir.status,
          conta_id: d.conta_id || null,
          categoria_id: modalDividir.categoria_id,
          venda_id: modalDividir.venda_id,
          total_parcelas: 1,
          modo_lancamento: 'simples',
        });
      }
      setModalDividir(null);
      recarregarLancamentos(); carregarDashboard();
      alert('Pagamento dividido em ' + validas.length + ' lançamentos!');
    } catch (err) { alert('Erro: ' + (err.response?.data?.error || err.message)); }
  };

  const importarOFX = async (e) => {
    const file = e.target.files[0];
    if (!file || !ofxContaId) { alert('Selecione a conta e o arquivo'); return; }
    setOfxLoading(true);
    try {
      const fd = new FormData();
      fd.append('arquivo', file); fd.append('conta_id', ofxContaId);
      const r = await api.post('/financeiro/importar-ofx', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setOfxTransacoes(r.data.transacoes.map(t => ({ ...t, ignorar: t.ja_importado })));
      // `vendas` é carregado no mount da página; recarrega aqui para o saldo em aberto
      // partir do estado atual do banco, não de um snapshot velho.
      api.get('/vendas').then(rv => setVendas(rv.data)).catch(console.error);
    } catch (err) { alert('Erro ao importar'); }
    finally { setOfxLoading(false); }
  };

  // Baixas já vinculadas a uma venda em OUTRAS linhas DESTA sessão de conciliação.
  // Os vínculos só vão pro banco no confirmarOFX (um único POST), então o
  // valor_recebido do array `vendas` não conhece os PIX marcados aqui na tela —
  // sem isto o 2º PIX do mesmo cliente ainda mostraria o saldo cheio da venda.
  const baixasNaSessao = (vendaId, exceptIdx) =>
    ofxTransacoes.reduce((s, tx, idx) => (
      idx !== exceptIdx && !tx.ignorar && modoLinha[idx] === 'venda'
        && String(dadosLinha[idx]?.venda_id || '') === String(vendaId)
        ? s + parseFloat(tx.valor || 0) : s
    ), 0);

  // Saldo em aberto da venda: banco (valor_final - valor_recebido) menos os
  // vínculos pendentes desta sessão.
  const saldoVenda = (v, exceptIdx) =>
    parseFloat(v?.valor_final || 0) - parseFloat(v?.valor_recebido || 0) - baixasNaSessao(v?.id, exceptIdx);

  const confirmarOFX = async () => {
    try {
      // Enriquecer transações com dados de vínculo e categorização
      const transacoesEnriquecidas = ofxTransacoes.map((t, i) => ({
        ...t,
        modo: modoLinha[i] || null,
        ...(dadosLinha[i] || {}),
      }));
      const r = await api.post('/financeiro/confirmar-ofx', { conta_id: ofxContaId, transacoes: transacoesEnriquecidas });
      alert(`${r.data.importados} lançamentos importados!`);
      setOfxTransacoes([]); setModoLinha({}); setDadosLinha({}); setBuscaCliente({});
      if (ofxRef.current) ofxRef.current.value = '';
      recarregarLancamentos(); carregarDashboard();
    } catch (err) { alert('Erro: ' + (err.response?.data?.error || err.message)); }
  };

  const inp = (label, campo, tipo, opts) => (
    <div style={{ marginBottom: '12px' }}>
      <label style={lbl}>{label}</label>
      {opts ? (
        <select value={form[campo] || ''} onChange={e => setV(campo, e.target.value)} style={inpStyle}>
          <option value=''>Selecione</option>
          {opts.map(o => <option key={o.v} value={o.v}>{o.l}</option>)}
        </select>
      ) : (
        <input type={tipo || 'text'} value={form[campo] || ''} onChange={e => setV(campo, e.target.value)} style={inpStyle} />
      )}
    </div>
  );

  // Filtrar lançamentos localmente
  const filtrados = lancamentos.filter(l => {
    if (!busca) return true;
    const b = busca.toLowerCase();
    return (l.descricao || '').toLowerCase().includes(b) || (l.categoria_nome || '').toLowerCase().includes(b) || (l.conta_nome || '').toLowerCase().includes(b) || (l.fornecedor_nome || '').toLowerCase().includes(b);
  });

  const toggleSel = (id) => setSelecionados(s => s.includes(id) ? s.filter(x => x !== id) : [...s, id]);
  const toggleTodos = () => setSelecionados(s => s.length === filtrados.length ? [] : filtrados.map(l => l.id));

  const seloParcial = <span style={{ background: '#E6F1FB', color: '#1A3A5C', padding: '2px 8px', borderRadius: '20px', fontSize: '10px', fontWeight: '700', marginLeft: '6px', whiteSpace: 'nowrap' }}>Parcial</span>;
  const resumoTitulo = (l) => (
    <div style={{ fontSize: '11px', color: '#6B7A8D', fontWeight: '400', marginTop: '2px' }}>
      Valor total {fmt(l.valor_total)} · Pago <span style={{ color: '#2D7D4F' }}>{fmt(l.pago)}</span> · Em aberto <span style={{ color: '#B06000', fontWeight: '600' }}>{fmt(l.em_aberto)}</span>
    </div>
  );
  const statusBadge = (status, venc) => {
    const atrasado = status === 'pendente' && venc && String(venc).split('T')[0] < hoje;
    const cfg = status === 'pago' ? { bg: '#EAF5EE', c: '#2D7D4F', t: '✓ Pago' } : status === 'fracionado' ? { bg: '#ECEFF3', c: '#6B7A8D', t: 'Fracionado' } : atrasado ? { bg: '#FDECEA', c: '#C0392B', t: '⚠ Vencido' } : { bg: '#FFF3E0', c: '#B06000', t: 'Pendente' };
    return <span style={{ background: cfg.bg, color: cfg.c, padding: '2px 8px', borderRadius: '20px', fontSize: '11px', fontWeight: '600' }}>{cfg.t}</span>;
  };

  const modo = form.modo_lancamento || 'simples';
  const valorParcela = parseFloat(form.valor_parcela || 0);
  const numParcelas = parseInt(form.total_parcelas || 1);

  const tabelaJSX = (
    <div>
      <div style={{ display: 'flex', gap: '10px', marginBottom: '10px', alignItems: 'center' }}>
        <input placeholder='Buscar por descrição, categoria, conta ou fornecedor...' value={busca}
          onChange={e => setBusca(e.target.value)}
          style={{ flex: 1, padding: '8px 12px', background: '#fff', border: '0.5px solid rgba(26,58,92,0.15)', borderRadius: '7px', fontSize: '13px', fontFamily: "'DM Sans',system-ui,sans-serif", color: '#1C2533', outline: 'none' }} />
        {selecionados.length > 0 && !somenteLeitura && (
          <button onClick={cancelarLote}
            style={{ padding: '8px 16px', background: '#C0392B', border: 'none', color: '#fff', borderRadius: '7px', cursor: 'pointer', fontFamily: "'DM Sans',system-ui,sans-serif", fontWeight: '700', fontSize: '13px', whiteSpace: 'nowrap' }}>
            × Cancelar {selecionados.length} selecionado(s)
          </button>
        )}
      </div>
      <div style={card}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th style={{ ...th, width: '32px' }}>
                <input type='checkbox' onChange={toggleTodos} checked={filtrados.length > 0 && selecionados.length === filtrados.length} />
              </th>
              {['Fornecedor', 'Descrição', 'Categoria', 'Vencimento', 'Pago em', 'Valor', 'Conta', 'Status', ''].map(h => <th key={h} style={th}>{h}</th>)}
            </tr>
          </thead>
          <tbody>
            {filtrados.length === 0 && <tr><td colSpan={9} style={{ ...td, textAlign: 'center', color: '#9AA3AE', padding: '32px' }}>Nenhum lançamento encontrado</td></tr>}
            {filtrados.map(l => (
              <tr key={l.id} style={{ background: selecionados.includes(l.id) ? '#E6F1FB' : 'transparent' }}>
                <td style={td}><input type='checkbox' checked={selecionados.includes(l.id)} onChange={() => toggleSel(l.id)} /></td>
                <td style={{ ...td, color: '#6B7A8D', fontSize: '12px' }}>{l.fornecedor_nome || '—'}</td>
                <td style={{ ...td, fontWeight: '500', cursor: 'pointer' }} onClick={() => abrirTitulo(l.id)} title='Abrir detalhe'>
                  {l.descricao}
                  {l.total_parcelas > 1 && <span style={{ fontSize: '10px', color: '#9AA3AE', marginLeft: '6px' }}>{l.parcela_atual}/{l.total_parcelas}</span>}
                  {l.parcial && seloParcial}
                  {l.fracionado && resumoTitulo(l)}
                </td>
                <td style={td}>
                  {l.categoria_nome && <span style={{ background: corCategoria(l.categoria_nome, l.categoria_cor) + '1F', color: corCategoria(l.categoria_nome, l.categoria_cor), padding: '3px 10px', borderRadius: '20px', fontSize: '11px', fontWeight: '600', whiteSpace: 'nowrap' }}>{l.categoria_nome}</span>}
                </td>
                <td style={{ ...td, fontFamily: "'DM Mono',monospace", fontSize: '12px', color: '#6B7A8D' }}>{fmtData(l.data_vencimento)}</td>
                <td style={{ ...td, fontFamily: "'DM Mono',monospace", fontSize: '12px', color: l.data_pagamento ? '#2D7D4F' : '#9AA3AE' }}>{l.data_pagamento ? fmtData(l.data_pagamento) : '—'}</td>
                <td style={{ ...td, fontFamily: "'DM Mono',monospace", fontSize: '13px', fontWeight: '700', color: l.tipo === 'receita' ? '#2D7D4F' : '#C0392B' }}>
                  {l.tipo === 'receita' ? '+' : '-'}{fmt(l.fracionado ? l.valor_total : l.valor)}
                </td>
                <td style={{ ...td, color: '#6B7A8D', fontSize: '12px' }}>{l.conta_nome || '—'}</td>
                <td style={td}>{l.fracionado ? (l.parcial ? seloParcial : statusBadge(l.status_titulo, l.data_vencimento)) : statusBadge(l.status, l.data_vencimento)}</td>
                <td style={{ ...td, whiteSpace: 'nowrap' }}>
                  {!somenteLeitura && l.fracionado && <div style={{ display: 'flex', gap: '4px' }}>
                    {l.em_aberto > 0 && (
                      <button onClick={() => abrirPagar(l)}
                        style={{ padding: '3px 8px', background: '#EAF5EE', border: 'none', color: '#2D7D4F', borderRadius: '5px', cursor: 'pointer', fontSize: '11px', fontWeight: '600', fontFamily: "'DM Sans',system-ui,sans-serif" }}>
                        Pagar
                      </button>
                    )}
                    <button onClick={() => abrirTitulo(l.id)}
                      style={{ padding: '3px 8px', background: 'transparent', border: '1px solid rgba(26,58,92,0.2)', color: '#1A3A5C', borderRadius: '5px', cursor: 'pointer', fontSize: '11px', fontFamily: "'DM Sans',system-ui,sans-serif" }}>
                      Abrir
                    </button>
                  </div>}
                  {!somenteLeitura && !l.fracionado && <div style={{ display: 'flex', gap: '4px' }}>
                    {l.status === 'pendente' && (
                      <button onClick={() => abrirPagar(l)}
                        style={{ padding: '3px 8px', background: '#EAF5EE', border: 'none', color: '#2D7D4F', borderRadius: '5px', cursor: 'pointer', fontSize: '11px', fontWeight: '600', fontFamily: "'DM Sans',system-ui,sans-serif" }}>
                        Pagar
                      </button>
                    )}
                    <button onClick={() => { setForm({ ...l, conta_id: l.conta_id ? String(l.conta_id) : '', categoria_id: l.categoria_id ? String(l.categoria_id) : '', fornecedor_id: l.fornecedor_id ? String(l.fornecedor_id) : '' }); setModalLanc(true); }}
                      style={{ padding: '3px 8px', background: 'transparent', border: '1px solid rgba(26,58,92,0.2)', color: '#1A3A5C', borderRadius: '5px', cursor: 'pointer', fontSize: '11px', fontFamily: "'DM Sans',system-ui,sans-serif" }}>
                      Editar
                    </button>
                      <button onClick={() => abrirDividir(l)}
                      style={{ padding: '3px 8px', background: '#E6F1FB', border: 'none', color: '#1A3A5C', borderRadius: '5px', cursor: 'pointer', fontSize: '11px', fontFamily: "'DM Sans',system-ui,sans-serif", fontWeight: '600' }}>
                      ÷ Dividir
                    </button>
                    <button onClick={() => cancelarUm(l.id)}
                      style={{ padding: '3px 8px', background: '#FDECEA', border: 'none', color: '#C0392B', borderRadius: '5px', cursor: 'pointer', fontSize: '11px', fontFamily: "'DM Sans',system-ui,sans-serif" }}>
                      ×
                    </button>

                  </div>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  const saldosBancosJSX = dashboard && (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(180px,1fr))', gap: '12px', marginBottom: '16px' }}>
      {dashboard.contas.map(c => (
        <div key={c.id} style={kpiBanco}>
          <div style={kpiBancoLbl}>{c.nome}</div>
          <div style={kpiBancoVal(c.saldo_atual)}>{fmt(c.saldo_atual)}</div>
        </div>
      ))}
      <div style={{ ...kpiBanco, background: '#0B1628', borderLeft: '3px solid #1A6DFF' }}>
        <div style={kpiBancoLbl}>Saldo Total</div>
        <div style={kpiBancoVal(dashboard.saldo_total)}>{fmt(dashboard.saldo_total)}</div>
      </div>
    </div>
  );

  const filtroJSX = (tipoFixo) => (
    <div style={{ display: 'flex', gap: '10px', marginBottom: '14px', flexWrap: 'wrap', alignItems: 'center' }}>
      <input type='date' value={filtro.inicio} onChange={e => setFiltro(f => ({ ...f, inicio: e.target.value, tipo: tipoFixo || f.tipo }))} style={{ ...inpStyle, width: 'auto' }} />
      <input type='date' value={filtro.fim} onChange={e => setFiltro(f => ({ ...f, fim: e.target.value, tipo: tipoFixo || f.tipo }))} style={{ ...inpStyle, width: 'auto' }} />
      <select value={filtro.status} onChange={e => setFiltro(f => ({ ...f, status: e.target.value, tipo: tipoFixo || f.tipo }))} style={{ ...inpStyle, width: 'auto' }}>
        <option value=''>Todos os status</option>
        <option value='pendente'>Pendente</option>
        <option value='pago'>{tipoFixo === 'receita' ? 'Recebido' : 'Pago'}</option>
      </select>
      {!tipoFixo && (
        <select value={filtro.conta_id} onChange={e => setFiltro(f => ({ ...f, conta_id: e.target.value }))} style={{ ...inpStyle, width: 'auto' }}>
          <option value=''>Todas as contas</option>
          {contas.map(c => <option key={c.id} value={c.id}>{c.nome}</option>)}
        </select>
      )}
      <button onClick={() => carregarLancamentos({ ...filtro, tipo: tipoFixo || filtro.tipo })}
        style={{ padding: '8px 18px', background: '#1A6DFF', border: 'none', color: '#fff', borderRadius: '8px', cursor: 'pointer', fontWeight: '600', fontSize: '13px', fontFamily: "'DM Sans',system-ui,sans-serif", whiteSpace: 'nowrap' }}>
        Buscar
      </button>
    </div>
  );

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <div style={{ fontSize: '18px', fontWeight: '700', color: '#1C2533' }}>Financeiro</div>
        <div style={{ display: 'flex', gap: '8px' }}>
          {!somenteLeitura && <button onClick={() => setModalTransf(true)}
            style={{ padding: '9px 18px', background: 'transparent', border: '1px solid rgba(26,58,92,0.3)', color: '#1A3A5C', borderRadius: '8px', cursor: 'pointer', fontWeight: '600', fontSize: '13px', fontFamily: "'DM Sans',system-ui,sans-serif" }}>
            ⇄ Transferência
          </button>}
          {!somenteLeitura && <button onClick={() => { setForm({ tipo: aba === 'A Receber' ? 'receita' : 'despesa', total_parcelas: 1, data_vencimento: hoje, modo_lancamento: 'simples' }); setModalLanc(true); }}
            style={{ padding: '9px 18px', background: '#1A6DFF', border: 'none', color: '#fff', borderRadius: '8px', cursor: 'pointer', fontWeight: '600', fontSize: '13px', fontFamily: "'DM Sans',system-ui,sans-serif" }}>
            + Novo Lançamento
          </button>}
        </div>
      </div>

      <div style={{ display: 'flex', gap: '4px', marginBottom: '18px', borderBottom: '1px solid rgba(26,58,92,0.1)' }}>
        {abas.map(a => (
          <button key={a} onClick={() => setAba(a)}
            style={{ padding: '8px 14px', background: 'none', border: 'none', borderBottom: aba === a ? '2px solid #1A6DFF' : '2px solid transparent', color: aba === a ? '#1A6DFF' : '#6B7A8D', cursor: 'pointer', fontWeight: aba === a ? '700' : '400', fontSize: '13px', fontFamily: "'DM Sans',system-ui,sans-serif", marginBottom: '-1px', whiteSpace: 'nowrap' }}>
            {a}
          </button>
        ))}
      </div>

      {/* DASHBOARD */}
      {aba === 'Dashboard' && dashboard && (
        <div>
          {saldosBancosJSX}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(150px,1fr))', gap: '12px', marginBottom: '14px' }}>
            {(painel.status === 'off' ? [
              { label: 'A Receber (30d)', val: dashboard.a_receber.total, qtd: dashboard.a_receber.qtd, c: '#2D7D4F' },
              { label: 'A Pagar (30d)', val: dashboard.a_pagar.total, qtd: dashboard.a_pagar.qtd, c: '#C0392B' },
              { label: 'Vencidos a receber', val: dashboard.vencidos_receber.total, qtd: dashboard.vencidos_receber.qtd, c: '#E67E22' },
              { label: 'Vencidos a pagar', val: dashboard.vencidos_pagar.total, qtd: dashboard.vencidos_pagar.qtd, c: '#E67E22' },
            ] : painel.status === 'ok' ? [
              // mesmos totais do painel abaixo (GET /financeiro/painel-contas): 30d = próximos SEM vencidos
              { label: 'A Receber (30d)', val: painel.dados.entrada.totais.proximos.total, qtd: painel.dados.entrada.totais.proximos.qtd, c: '#2D7D4F' },
              { label: 'A Pagar (30d)', val: painel.dados.saida.totais.proximos.total, qtd: painel.dados.saida.totais.proximos.qtd, c: '#C0392B' },
              { label: 'Vencidos a receber', val: painel.dados.entrada.totais.vencido.total, qtd: painel.dados.entrada.totais.vencido.qtd, c: '#E67E22' },
              { label: 'Vencidos a pagar', val: painel.dados.saida.totais.vencido.total, qtd: painel.dados.saida.totais.vencido.qtd, c: '#E67E22' },
            ] : ['A Receber (30d)', 'A Pagar (30d)', 'Vencidos a receber', 'Vencidos a pagar'].map(label => ({ label, val: null, qtd: null, c: '#9AA3AE', txt: painel.status === 'erro' ? 'erro' : '…' }))
            ).concat([
              { label: 'Resultado do mês', val: dashboard.resultado_mes, qtd: null, c: parseFloat(dashboard.resultado_mes) >= 0 ? '#2D7D4F' : '#C0392B' },
            ]).map(item => (
              <div key={item.label} style={card}>
                <div style={{ fontSize: '11px', color: '#6B7A8D', fontWeight: '600', textTransform: 'uppercase', marginBottom: '6px' }}>{item.label}</div>
                <div style={{ fontSize: 'clamp(15px, 4vw, 18px)', fontWeight: '700', fontFamily: "'DM Mono',monospace", color: item.c, whiteSpace: 'nowrap' }}>{item.txt || fmt(item.val)}</div>
                {item.qtd !== null && <div style={{ fontSize: '11px', color: '#9AA3AE', marginTop: '3px' }}>{item.qtd} lançamentos</div>}
              </div>
            ))}
          </div>
          {painel.status === 'erro' && (
            <div style={{ ...card, marginBottom: '14px', borderLeft: '3px solid #C0392B', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px' }}>
              <span style={{ fontSize: '13px', color: '#C0392B' }}>Não foi possível carregar Recebimentos / Pagamentos: {painel.erro}</span>
              <button onClick={carregarPainel} style={{ padding: '6px 14px', background: '#C0392B', border: 'none', color: '#fff', borderRadius: '7px', cursor: 'pointer', fontSize: '12px', fontWeight: '600', fontFamily: "'DM Sans',system-ui,sans-serif", whiteSpace: 'nowrap' }}>Tentar novamente</button>
            </div>
          )}
          {painel.status === 'ok' && (
            <PainelContas dados={painel.dados} carregando={painelCarregando} somenteLeitura={somenteLeitura}
              onBaixar={baixarDoPainel} onAbrir={abrirDoPainel} onVerTodos={verTodosDoPainel} onMudarMes={mudarMesPainel} />
          )}
          <div style={{ display: 'grid', gridTemplateColumns: painel.status === 'off' ? '1fr 1fr' : 'repeat(auto-fit,minmax(320px,1fr))', gap: '14px' }}>
            <div style={card}>
              <div style={{ fontSize: '13px', fontWeight: '700', color: '#1C2533', marginBottom: '14px' }}>Mês atual</div>
              {[{ label: '↑ Receitas', val: dashboard.receita_mes, bg: '#EAF5EE', c: '#2D7D4F' }, { label: '↓ Despesas', val: dashboard.despesa_mes, bg: '#FDECEA', c: '#C0392B' }].map(item => (
                <div key={item.label} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 12px', background: item.bg, borderRadius: '8px', marginBottom: '8px' }}>
                  <span style={{ fontSize: '13px', color: item.c, fontWeight: '600' }}>{item.label}</span>
                  <span style={{ fontFamily: "'DM Mono',monospace", fontWeight: '700', color: item.c }}>{fmt(item.val)}</span>
                </div>
              ))}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 12px', background: '#1A2332', borderRadius: '8px' }}>
                <span style={{ fontSize: '13px', color: '#fff', fontWeight: '600' }}>= Resultado</span>
                <span style={{ fontFamily: "'DM Mono',monospace", fontWeight: '700', color: parseFloat(dashboard.resultado_mes) >= 0 ? '#7DF5B8' : '#FF8080' }}>{fmt(dashboard.resultado_mes)}</span>
              </div>
            </div>
            {painel.status === 'off' && (<>
            <div style={card}>
              <div style={{ fontSize: '13px', fontWeight: '700', color: '#1C2533', marginBottom: '14px' }}>Próximos 30 dias</div>
              <div style={{ maxHeight: '200px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '6px' }}>
                {dashboard.proximos_vencimentos.length === 0 && <div style={{ color: '#9AA3AE', fontSize: '13px' }}>Sem vencimentos</div>}
                {dashboard.proximos_vencimentos.map(l => (
                  <div key={l.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 10px', background: '#F4F6F9', borderRadius: '7px' }}>
                    <div><div style={{ fontSize: '12px', fontWeight: '500', color: '#1C2533' }}>{l.descricao}</div><div style={{ fontSize: '11px', color: '#9AA3AE' }}>{fmtData(l.data_vencimento)}</div></div>
                    <span style={{ fontFamily: "'DM Mono',monospace", fontSize: '13px', fontWeight: '700', color: l.tipo === 'receita' ? '#2D7D4F' : '#C0392B' }}>{l.tipo === 'receita' ? '+' : '-'}{fmt(l.valor)}</span>
                  </div>
                ))}
              </div>
            </div>
            <div style={card}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px' }}>
                <div style={{ fontSize: '13px', fontWeight: '700', color: '#1C2533' }}>A Receber no mês</div>
                <span style={{ background: '#EAF5EE', color: '#2D7D4F', padding: '2px 8px', borderRadius: '20px', fontSize: '11px', fontWeight: '700' }}>{fmt(dashboard.a_receber.total)}</span>
              </div>
              <div style={{ maxHeight: '200px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '6px' }}>
                {(dashboard.a_receber_lista || []).length === 0 && <div style={{ color: '#9AA3AE', fontSize: '13px' }}>Nenhum recebimento pendente</div>}
                {(dashboard.a_receber_lista || []).map(l => (
                  <div key={l.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 10px', background: '#F4F6F9', borderRadius: '7px' }}>
                    <div><div style={{ fontSize: '12px', fontWeight: '500', color: '#1C2533' }}>{l.descricao}</div><div style={{ fontSize: '11px', color: '#9AA3AE' }}>{fmtData(l.data_vencimento)}</div></div>
                    <span style={{ fontFamily: "'DM Mono',monospace", fontSize: '13px', fontWeight: '700', color: '#2D7D4F' }}>+{fmt(l.valor)}</span>
                  </div>
                ))}
              </div>
            </div>
            </>)}
          </div>
        </div>
      )}

      {aba === 'A Pagar' && <div>{saldosBancosJSX}{filtroJSX('despesa')}{tabelaJSX}</div>}
      {aba === 'A Receber' && <div>{saldosBancosJSX}{filtroJSX('receita')}{tabelaJSX}</div>}
      {aba === 'Lançamentos' && <div>{filtroJSX()}{tabelaJSX}</div>}

      {/* FORNECEDORES */}
      {aba === 'Fornecedores' && (
        <div style={{ display: 'grid', gridTemplateColumns: fornSel ? '1fr 1fr' : '1fr', gap: '14px', alignItems: 'start' }}>
          {/* Lista de fornecedores */}
          <div>
            <div style={{ marginBottom: '10px' }}>
              <input placeholder='Buscar fornecedor...' value={buscaForn} onChange={e => setBuscaForn(e.target.value)}
                style={{ width: '100%', padding: '8px 12px', background: '#fff', border: '0.5px solid rgba(26,58,92,0.15)', borderRadius: '7px', fontSize: '13px', fontFamily: "'DM Sans',system-ui,sans-serif", color: '#1C2533', outline: 'none', boxSizing: 'border-box' }} />
            </div>
            <div style={card}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    <th style={th}>Fornecedor</th>
                    <th style={{ ...th, textAlign: 'right' }}>Em aberto</th>
                    <th style={{ ...th, textAlign: 'right' }}>Vencido</th>
                    <th style={{ ...th, textAlign: 'right' }}>Pago no período</th>
                  </tr>
                </thead>
                <tbody>
                  {fornResumo.filter(f => !buscaForn || (f.nome || '').toLowerCase().includes(buscaForn.toLowerCase())).length === 0 && (
                    <tr><td colSpan={4} style={{ ...td, textAlign: 'center', color: '#9AA3AE', padding: '32px' }}>Nenhum fornecedor encontrado</td></tr>
                  )}
                  {fornResumo.filter(f => !buscaForn || (f.nome || '').toLowerCase().includes(buscaForn.toLowerCase())).map(f => (
                    <tr key={f.id} onClick={() => abrirFornecedor(f)}
                      style={{ cursor: 'pointer', background: fornSel?.id === f.id ? '#E6F1FB' : 'transparent' }}>
                      <td style={{ ...td, fontWeight: '600' }}>
                        {f.nome}
                        {f.cpf_cnpj && <div style={{ fontSize: '11px', color: '#9AA3AE', fontWeight: '400' }}>{f.cpf_cnpj}</div>}
                      </td>
                      <td style={{ ...td, textAlign: 'right', fontFamily: "'DM Mono',monospace", fontWeight: '700', color: parseFloat(f.total_aberto) > 0 ? '#B06000' : '#9AA3AE' }}>
                        {fmt(f.total_aberto)}
                        {parseInt(f.qtd_aberto) > 0 && <div style={{ fontSize: '10px', color: '#9AA3AE', fontWeight: '400' }}>{f.qtd_aberto} conta(s)</div>}
                      </td>
                      <td style={{ ...td, textAlign: 'right', fontFamily: "'DM Mono',monospace", fontWeight: '700', color: parseFloat(f.total_vencido) > 0 ? '#C0392B' : '#9AA3AE' }}>
                        {fmt(f.total_vencido)}
                        {parseInt(f.qtd_vencido) > 0 && <div style={{ fontSize: '10px', color: '#C0392B', fontWeight: '400' }}>{f.qtd_vencido} vencida(s)</div>}
                      </td>
                      <td style={{ ...td, textAlign: 'right', fontFamily: "'DM Mono',monospace", fontWeight: '700', color: '#2D7D4F' }}>{fmt(f.total_pago_periodo)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Painel lateral do fornecedor */}
          {fornSel && (
            <div style={{ ...card, position: 'sticky', top: '12px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '14px' }}>
                <div>
                  <div style={{ fontSize: '16px', fontWeight: '700', color: '#1C2533' }}>{fornSel.nome}</div>
                  {fornSel.cpf_cnpj && <div style={{ fontSize: '12px', color: '#9AA3AE' }}>{fornSel.cpf_cnpj}</div>}
                </div>
                <div style={{ flex: 1 }} />
                {!somenteLeitura && <button onClick={agendarPagamentoFornecedor}
                  style={{ padding: '6px 12px', background: '#1A6DFF', border: 'none', color: '#fff', borderRadius: '8px', cursor: 'pointer', fontSize: '12px', fontWeight: '600', marginRight: '8px', fontFamily: "'DM Sans',system-ui,sans-serif" }}>
                  + Agendar pagamento
                </button>}
                <button onClick={() => { setFornSel(null); setFornDetalhe(null); }} style={{ background: 'none', border: 'none', fontSize: '20px', color: '#9AA3AE', cursor: 'pointer' }}>×</button>
              </div>

              {/* Resumo */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: '8px', marginBottom: '16px' }}>
                {[
                  { l: 'Em aberto', v: fornSel.total_aberto, c: '#B06000' },
                  { l: 'Vencido', v: fornSel.total_vencido, c: '#C0392B' },
                  { l: 'Pago no período', v: fornSel.total_pago_periodo, c: '#2D7D4F' },
                ].map(it => (
                  <div key={it.l} style={{ background: '#F4F6F9', borderRadius: '8px', padding: '8px 10px' }}>
                    <div style={{ fontSize: '10px', color: '#9AA3AE', textTransform: 'uppercase', fontWeight: '600', marginBottom: '3px' }}>{it.l}</div>
                    <div style={{ fontSize: '14px', fontWeight: '700', fontFamily: "'DM Mono',monospace", color: it.c }}>{fmt(it.v)}</div>
                  </div>
                ))}
              </div>

              {fornLoading && <div style={{ color: '#9AA3AE', fontSize: '13px' }}>Carregando...</div>}

              {fornDetalhe && (
                <>
                  {/* Barra de ações em lote */}
                  {selLanc.length > 0 && !somenteLeitura && (
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#E6F1FB', borderRadius: '8px', padding: '8px 12px', marginBottom: '12px' }}>
                      <span style={{ fontSize: '12px', color: '#1A3A5C', fontWeight: '600' }}>{selLanc.length} selecionado(s)</span>
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <button onClick={() => { setPagLote({ data_pagamento: hoje, conta_id: '' }); setModalPagarLote(true); }}
                          style={{ padding: '6px 12px', background: '#2D7D4F', border: 'none', color: '#fff', borderRadius: '8px', cursor: 'pointer', fontSize: '12px', fontWeight: '600', fontFamily: "'DM Sans',system-ui,sans-serif" }}>
                          Marcar como pago
                        </button>
                        <button onClick={() => { setNovaVencLote(hoje); setModalVencLote(true); }}
                          style={{ padding: '6px 12px', background: '#1A6DFF', border: 'none', color: '#fff', borderRadius: '8px', cursor: 'pointer', fontSize: '12px', fontWeight: '600', fontFamily: "'DM Sans',system-ui,sans-serif" }}>
                          Alterar vencimento
                        </button>
                        <button onClick={() => { setNovoValorLote(''); setModalValorLote(true); }}
                          style={{ padding: '6px 12px', background: '#1A6DFF', border: 'none', color: '#fff', borderRadius: '8px', cursor: 'pointer', fontSize: '12px', fontWeight: '600', fontFamily: "'DM Sans',system-ui,sans-serif" }}>
                          Alterar valor
                        </button>
                        {isAdmin && (
                          <button onClick={excluirLoteFornecedor}
                            style={{ padding: '6px 12px', background: '#C0392B', border: 'none', color: '#fff', borderRadius: '6px', cursor: 'pointer', fontSize: '12px', fontWeight: '700', fontFamily: "'DM Sans',system-ui,sans-serif" }}>
                            Excluir
                          </button>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Próximas contas a pagar */}
                  <div style={{ fontSize: '13px', fontWeight: '700', color: '#1C2533', marginBottom: '8px' }}>
                    Próximas contas a pagar <span style={{ color: '#9AA3AE', fontWeight: '400' }}>({fornDetalhe.proximas.length})</span>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginBottom: '18px', maxHeight: '320px', overflowY: 'auto' }}>
                    {fornDetalhe.proximas.length === 0 && <div style={{ color: '#9AA3AE', fontSize: '13px' }}>Nenhuma conta pendente</div>}
                    {fornDetalhe.proximas.map(l => {
                      const vencido = String(l.data_vencimento).split('T')[0] < hoje;
                      const emEdicao = editLanc?.id === l.id;
                      return (
                        <div key={l.id} style={{ border: `1px solid ${selLanc.includes(l.id) ? '#1A3A5C' : 'rgba(26,58,92,0.1)'}`, borderRadius: '8px', padding: '10px 12px', background: selLanc.includes(l.id) ? '#F0F5FF' : '#fff' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <input type='checkbox' checked={selLanc.includes(l.id)}
                              onChange={() => setSelLanc(s => s.includes(l.id) ? s.filter(x => x !== l.id) : [...s, l.id])} />
                            <div style={{ flex: 1, minWidth: 0, cursor: 'pointer' }} onClick={() => abrirTitulo(l.id)} title='Abrir detalhe'>
                              <div style={{ fontSize: '13px', fontWeight: '500', color: '#1C2533', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{l.descricao}{l.parcial && seloParcial}</div>
                              {l.fracionado && resumoTitulo(l)}
                              <div style={{ fontSize: '11px', color: vencido ? '#C0392B' : '#9AA3AE', fontWeight: vencido ? '600' : '400' }}>
                                Vence {fmtData(l.data_vencimento)}{vencido ? ' · vencida' : ''}
                              </div>
                            </div>
                            <div style={{ fontFamily: "'DM Mono',monospace", fontSize: '13px', fontWeight: '700', color: '#C0392B', whiteSpace: 'nowrap' }}>{fmt(l.em_aberto ?? l.valor)}</div>
                            {!somenteLeitura && <button onClick={() => { if (l.fracionado) { abrirTitulo(l.id); return; } setEditLanc(l); setEditForm({ descricao: l.descricao || '', data_vencimento: String(l.data_vencimento).split('T')[0], valor: l.valor }); }}
                              style={{ padding: '3px 8px', background: 'transparent', border: '1px solid rgba(26,58,92,0.2)', color: '#1A3A5C', borderRadius: '5px', cursor: 'pointer', fontSize: '11px', fontFamily: "'DM Sans',system-ui,sans-serif" }}>Editar</button>}
                            {(l.status === 'pendente' || l.fracionado) && !somenteLeitura && (
                              <button onClick={() => abrirPagar(l)} title='Pagar'
                                style={{ padding: '3px 8px', background: 'transparent', border: '1px solid rgba(45,125,79,0.4)', color: '#2D7D4F', borderRadius: '5px', cursor: 'pointer', fontSize: '11px', fontWeight: '600', fontFamily: "'DM Sans',system-ui,sans-serif" }}>Pagar</button>
                            )}
                            {(l.status === 'pendente' || l.fracionado) && !somenteLeitura && (
                              <button onClick={() => excluirLancFornecedor(l)} title={l.fracionado ? 'Excluir o valor em aberto' : 'Excluir lançamento'}
                                style={{ padding: '3px 8px', background: 'transparent', border: '1px solid rgba(192,57,43,0.35)', color: '#C0392B', borderRadius: '5px', cursor: 'pointer', fontSize: '13px', lineHeight: 1, fontWeight: '700', fontFamily: "'DM Sans',system-ui,sans-serif" }}>×</button>
                            )}
                          </div>
                          {emEdicao && (
                            <div style={{ display: 'flex', gap: '8px', marginTop: '10px', alignItems: 'flex-end', flexWrap: 'wrap' }}>
                              <div style={{ flexBasis: '100%' }}>
                                <label style={lbl}>Descrição</label>
                                <input value={editForm.descricao || ''} onChange={e => setEditForm(f => ({ ...f, descricao: e.target.value }))} style={inpStyle} />
                              </div>
                              <div style={{ flex: 1 }}>
                                <label style={lbl}>Vencimento</label>
                                <input type='date' value={editForm.data_vencimento || ''} onChange={e => setEditForm(f => ({ ...f, data_vencimento: e.target.value }))} style={inpStyle} />
                              </div>
                              <div style={{ flex: 1 }}>
                                <label style={lbl}>Valor</label>
                                <input type='number' step='0.01' value={editForm.valor || ''} onChange={e => setEditForm(f => ({ ...f, valor: e.target.value }))} style={inpStyle} />
                              </div>
                              <button onClick={salvarEdicaoLanc} style={{ padding: '8px 12px', background: '#1A6DFF', border: 'none', color: '#fff', borderRadius: '8px', cursor: 'pointer', fontSize: '12px', fontWeight: '600', fontFamily: "'DM Sans',system-ui,sans-serif" }}>Salvar</button>
                              <button onClick={() => { setEditLanc(null); setEditForm({}); }} style={{ padding: '8px 10px', background: '#F4F6F9', border: 'none', color: '#6B7A8D', borderRadius: '6px', cursor: 'pointer', fontSize: '12px' }}>×</button>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>

                  {/* Últimas contas pagas */}
                  <div style={{ fontSize: '13px', fontWeight: '700', color: '#1C2533', marginBottom: '8px' }}>
                    Últimas 10 contas pagas <span style={{ color: '#9AA3AE', fontWeight: '400' }}>({fornDetalhe.pagas.length})</span>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', maxHeight: '260px', overflowY: 'auto' }}>
                    {fornDetalhe.pagas.length === 0 && <div style={{ color: '#9AA3AE', fontSize: '13px' }}>Nenhum pagamento registrado</div>}
                    {fornDetalhe.pagas.map(l => (
                      <div key={l.id} onClick={() => abrirTitulo(l.id)} title='Abrir detalhe' style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 12px', background: '#F4F6F9', borderRadius: '7px', cursor: 'pointer' }}>
                        <div style={{ minWidth: 0 }}>
                          <div style={{ fontSize: '13px', fontWeight: '500', color: '#1C2533', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{l.descricao}{l.parcial && seloParcial}</div>
                          {l.fracionado && resumoTitulo(l)}
                          <div style={{ fontSize: '11px', color: '#9AA3AE' }}>Venc. {fmtData(l.data_vencimento)} · {l.fracionado ? 'Último pagamento' : 'Pago em'} {fmtData(l.ultimo_pagamento || l.data_pagamento)}{!l.fracionado && l.conta_nome ? ' · ' + l.conta_nome : ''}</div>
                        </div>
                        <div style={{ fontFamily: "'DM Mono',monospace", fontSize: '13px', fontWeight: '700', color: '#2D7D4F', whiteSpace: 'nowrap', marginLeft: '8px' }}>{fmt(l.pago ?? l.valor)}</div>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      )}

      {/* BANCOS */}
      {aba === 'Bancos' && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '14px' }}>
            {!somenteLeitura && <button onClick={() => { setFormConta({}); setModalConta(true); }}
              style={{ padding: '9px 18px', background: '#1A6DFF', border: 'none', color: '#fff', borderRadius: '8px', cursor: 'pointer', fontWeight: '600', fontSize: '13px', fontFamily: "'DM Sans',system-ui,sans-serif" }}>
              + Nova Conta
            </button>}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(280px,1fr))', gap: '14px' }}>
            {contas.map(c => (
              <div key={c.id} style={{ ...card, borderLeft: `3px solid ${parseFloat(c.saldo_atual) >= 0 ? '#2D7D4F' : '#C0392B'}` }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
                  <div>
                    <div style={{ fontSize: '15px', fontWeight: '700', color: '#1C2533' }}>{c.nome}</div>
                    <div style={{ fontSize: '12px', color: '#9AA3AE' }}>{c.banco}{c.agencia ? ' · Ag: ' + c.agencia : ''}{c.conta ? ' · CC: ' + c.conta : ''}</div>
                    {c.data_saldo_inicial && <div style={{ fontSize: '11px', color: '#9AA3AE', marginTop: '2px' }}>Ref.: {fmtData(c.data_saldo_inicial)}</div>}
                  </div>
                  <div style={{ display: "flex", gap: "6px" }}>
                    <button onClick={() => abrirExtrato(c)}
                      style={{ padding: "4px 10px", background: "#E6F1FB", border: "none", color: "#1A3A5C", borderRadius: "6px", cursor: "pointer", fontSize: "11px", fontWeight: "600" }}>Extrato</button>
                    {!somenteLeitura && <button onClick={() => { setFormConta(c); setModalConta(true); }}
                      style={{ padding: "4px 10px", background: "transparent", border: "1px solid rgba(26,58,92,0.2)", color: "#1A3A5C", borderRadius: "6px", cursor: "pointer", fontSize: "11px" }}>Editar</button>}
                  </div>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <div><div style={{ fontSize: '11px', color: '#9AA3AE', marginBottom: '2px' }}>Saldo atual</div><div style={{ fontSize: '22px', fontWeight: '700', fontFamily: "'DM Mono',monospace", color: parseFloat(c.saldo_atual) >= 0 ? '#2D7D4F' : '#C0392B' }}>{fmt(c.saldo_atual)}</div></div>
                  <div style={{ textAlign: 'right' }}><div style={{ fontSize: '11px', color: '#9AA3AE', marginBottom: '2px' }}>Saldo inicial</div><div style={{ fontSize: '14px', fontFamily: "'DM Mono',monospace", color: '#6B7A8D' }}>{fmt(c.saldo_inicial)}</div></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* IMPORTAR OFX */}
      {aba === 'Importar OFX' && (
        <div>
          <div style={{ ...card, marginBottom: '14px' }}>
            <div style={{ fontSize: '14px', fontWeight: '700', color: '#1C2533', marginBottom: '14px' }}>Importar extrato OFX</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: '10px', marginBottom: '8px' }}>
              <div><label style={lbl}>Conta bancária</label>
                <select value={ofxContaId} onChange={e => setOfxContaId(e.target.value)} style={inpStyle}>
                  <option value=''>Selecione</option>
                  {contas.map(ct => <option key={ct.id} value={ct.id}>{ct.nome}</option>)}
                </select>
              </div>
              <div><label style={lbl}>Arquivo OFX</label>
                <input type='file' accept='.ofx,.OFX' ref={ofxRef} onChange={importarOFX} style={{ ...inpStyle, padding: '6px 10px' }} />
              </div>
            </div>
            {ofxLoading && <div style={{ fontSize: '13px', color: '#9AA3AE' }}>Processando...</div>}
          </div>
          {ofxTransacoes.length > 0 && (
            <div style={card}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px' }}>
                <div style={{ fontSize: '14px', fontWeight: '700', color: '#1C2533' }}>
                  {ofxTransacoes.length} transações · <span style={{ color: '#1E5FA5' }}>{ofxTransacoes.filter(t => !t.ignorar).length} serão importadas</span>
                </div>
                <button onClick={confirmarOFX} style={{ padding: '9px 18px', background: '#2D7D4F', border: 'none', color: '#fff', borderRadius: '8px', cursor: 'pointer', fontWeight: '700', fontSize: '13px', fontFamily: "'DM Sans',system-ui,sans-serif" }}>✓ Confirmar importação</button>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {ofxTransacoes.map((t, i) => {
                  const modo = modoLinha[i];
                  const dados = dadosLinha[i] || {};
                  const clientesFiltrados = clientes.filter(cl =>
                    !buscaCliente[i] || cl.nome?.toLowerCase().includes((buscaCliente[i] || '').toLowerCase())
                  );
                  // Só vendas com saldo em aberto podem receber baixa — venda quitada
                  // na lista era convite a vincular o PIX na venda errada.
                  // Exceção: a venda JÁ selecionada nesta linha continua listada mesmo
                  // que os outros PIX da sessão a quitem, senão a seleção sumiria sozinha.
                  const vendasCliente = dados.cliente_id ? vendas.filter(v =>
                    String(v.cliente_id) === String(dados.cliente_id) && (
                      String(v.id) === String(dados.venda_id || '') || (
                        v.status_pagamento !== 'pago' &&
                        parseFloat(v.valor_recebido || 0) < parseFloat(v.valor_final || 0) - 0.01
                      )
                    )
                  ) : [];
                  return (
                    <div key={i} style={{ border: `1px solid ${t.ignorar ? 'rgba(26,58,92,0.08)' : modo ? '#1A3A5C' : 'rgba(26,58,92,0.12)'}`, borderRadius: '8px', padding: '12px 14px', opacity: t.ignorar ? 0.4 : 1, background: modo ? '#F0F5FF' : '#fff' }}>
                      {/* Linha principal */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <input type='checkbox' checked={!t.ignorar} disabled={t.ja_importado}
                          onChange={e => setOfxTransacoes(ts => ts.map((x, idx) => idx === i ? { ...x, ignorar: !e.target.checked } : x))} />
                        <div style={{ fontFamily: "'DM Mono',monospace", fontSize: '12px', color: '#6B7A8D', whiteSpace: 'nowrap' }}>{fmtData(t.data)}</div>
                        <div style={{ flex: 1, fontSize: '12px', color: '#1C2533', fontWeight: '500' }}>{t.memo}</div>
                        <span style={{ color: t.tipo === 'receita' ? '#2D7D4F' : '#C0392B', fontWeight: '600', fontSize: '12px', whiteSpace: 'nowrap' }}>{t.tipo}</span>
                        <div style={{ fontFamily: "'DM Mono',monospace", fontSize: '13px', fontWeight: '700', color: t.tipo === 'receita' ? '#2D7D4F' : '#C0392B', whiteSpace: 'nowrap' }}>{fmt(t.valor)}</div>
                        {t.ja_importado
                          ? <span style={{ color: '#9AA3AE', fontSize: '11px', whiteSpace: 'nowrap' }}>Já importado</span>
                          : !t.ignorar && (
                            <div style={{ display: 'flex', gap: '4px' }}>
                              <button onClick={() => setModoLinha(m => ({ ...m, [i]: m[i] === 'venda' ? null : 'venda' }))}
                                style={{ padding: '3px 8px', background: modo === 'venda' ? '#1A3A5C' : '#E6F1FB', border: 'none', color: modo === 'venda' ? '#fff' : '#1A3A5C', borderRadius: '5px', cursor: 'pointer', fontSize: '11px', fontFamily: "'DM Sans',system-ui,sans-serif", fontWeight: '600', whiteSpace: 'nowrap' }}>
                                🔗 Vincular venda
                              </button>
                              <button onClick={() => setModoLinha(m => ({ ...m, [i]: m[i] === 'manual' ? null : 'manual' }))}
                                style={{ padding: '3px 8px', background: modo === 'manual' ? '#1A3A5C' : '#F4F6F9', border: 'none', color: modo === 'manual' ? '#fff' : '#6B7A8D', borderRadius: '5px', cursor: 'pointer', fontSize: '11px', fontFamily: "'DM Sans',system-ui,sans-serif", whiteSpace: 'nowrap' }}>
                                ✏️ Categorizar
                              </button>
                            </div>
                          )
                        }
                      </div>

                      {/* Modo: Vincular a uma venda */}
                      {modo === 'venda' && !t.ignorar && (
                        <div style={{ marginTop: '10px', padding: '10px', background: '#E6F1FB', borderRadius: '7px' }}>
                          <div style={{ fontSize: '11px', fontWeight: '600', color: '#1A3A5C', marginBottom: '8px', textTransform: 'uppercase' }}>Vincular a uma venda</div>
                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                            <div>
                              <label style={{ ...lbl, color: '#1A3A5C' }}>Buscar cliente</label>
                              <input placeholder='Nome do cliente...' value={buscaCliente[i] || ''}
                                onChange={e => setBuscaCliente(b => ({ ...b, [i]: e.target.value }))}
                                style={{ ...inpStyle, background: '#fff' }} />
                              {buscaCliente[i] && clientesFiltrados.length > 0 && (
                                <div style={{ background: '#fff', border: '1px solid rgba(26,58,92,0.15)', borderRadius: '6px', maxHeight: '120px', overflowY: 'auto', marginTop: '2px' }}>
                                  {clientesFiltrados.slice(0, 5).map(cl => (
                                    <div key={cl.id} onClick={() => { setDadosLinha(d => ({ ...d, [i]: { ...d[i], cliente_id: cl.id, cliente_nome: cl.nome } })); setBuscaCliente(b => ({ ...b, [i]: cl.nome })); }}
                                      style={{ padding: '8px 10px', cursor: 'pointer', fontSize: '12px', borderBottom: '0.5px solid rgba(26,58,92,0.07)' }}
                                      onMouseOver={e => e.target.style.background='#F4F6F9'}
                                      onMouseOut={e => e.target.style.background='#fff'}>
                                      {cl.nome} — {cl.cpf_cnpj}
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                            <div>
                              <label style={{ ...lbl, color: '#1A3A5C' }}>Venda do cliente</label>
                              <select value={dados.venda_id || ''} onChange={e => setDadosLinha(d => ({ ...d, [i]: { ...d[i], venda_id: e.target.value } }))}
                                style={{ ...inpStyle, background: '#fff' }} disabled={!dados.cliente_id}>
                                <option value=''>Selecione a venda</option>
                                {vendasCliente.map(v => {
                                  const saldo = saldoVenda(v, i);
                                  return (
                                    <option key={v.id} value={v.id}>
                                      #{v.id} — {v.marca} {v.modelo} — {fmt(v.valor_final)}
                                      {Math.abs(saldo - parseFloat(v.valor_final || 0)) > 0.01 ? ` · saldo ${fmt(saldo)}` : ''}
                                    </option>
                                  );
                                })}
                              </select>
                            </div>
                          </div>
                          {dados.venda_id && (() => {
                            const vendaSel = vendasCliente.find(v => String(v.id) === String(dados.venda_id));
                            const valorPix = parseFloat(t.valor || 0);
                            // saldoAntes já desconta os PIX vinculados a esta venda nas outras linhas do extrato.
                            const saldoAntes = saldoVenda(vendaSel, i);
                            const saldoApos = saldoAntes - valorPix;
                            const excede = valorPix > saldoAntes + 0.01;
                            return (
                              <div style={{ marginTop: '8px', padding: '8px 10px', background: excede ? '#FDECEA' : '#EAF5EE', borderRadius: '6px' }}>
                                <div style={{ fontSize: '12px', color: excede ? '#C0392B' : '#2D7D4F', fontWeight: '600' }}>
                                  ✓ Baixa de {fmt(valorPix)} vinculada à venda #{dados.venda_id} · saldo em aberto {fmt(saldoAntes)}
                                </div>
                                {excede && (
                                  <div style={{ fontSize: '11px', color: '#C0392B', fontWeight: '600', marginTop: '3px' }}>
                                    ⚠ Excede o saldo em aberto desta venda — revise antes de confirmar
                                  </div>
                                )}
                                {!excede && saldoApos > 0.01 && (
                                  <div style={{ fontSize: '11px', color: '#B06000', fontWeight: '600', marginTop: '3px' }}>
                                    ⚠ Saldo restante: {fmt(saldoApos)} ficará pendente
                                  </div>
                                )}
                                {!excede && saldoApos <= 0.01 && (
                                  <div style={{ fontSize: '11px', color: '#2D7D4F', marginTop: '3px' }}>
                                    ✓ Venda será quitada integralmente
                                  </div>
                                )}
                              </div>
                            );
                          })()}
                        </div>
                      )}

                      {/* Modo: Categorizar manualmente */}
                      {modo === 'manual' && !t.ignorar && (
                        <div style={{ marginTop: '10px', padding: '10px', background: '#F4F6F9', borderRadius: '7px' }}>
                          <div style={{ fontSize: '11px', fontWeight: '600', color: '#6B7A8D', marginBottom: '8px', textTransform: 'uppercase' }}>Categorizar lançamento</div>
                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '8px' }}>
                            <div>
                              <label style={lbl}>Descrição</label>
                              <input value={dados.descricao || t.memo} onChange={e => setDadosLinha(d => ({ ...d, [i]: { ...d[i], descricao: e.target.value } }))} style={inpStyle} />
                            </div>
                            <div>
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '5px' }}>
                                <label style={{ ...lbl, marginBottom: 0 }}>Categoria</label>
                                <button type='button' onClick={() => setDadosLinha(d => ({ ...d, [i]: { ...d[i], _novaCategoria: !d[i]?._novaCategoria, _nomeCategoria: '', _tipoCategoria: t.tipo } }))}
                                  style={{ fontSize: '11px', color: '#1E5FA5', background: 'none', border: 'none', cursor: 'pointer', fontFamily: "'DM Sans',system-ui,sans-serif", fontWeight: '600' }}>+ Nova</button>
                              </div>
                              {dados._novaCategoria ? (
                                <div style={{ display: 'flex', gap: '4px' }}>
                                  <input placeholder='Nome...' value={dados._nomeCategoria || ''} onChange={e => setDadosLinha(d => ({ ...d, [i]: { ...d[i], _nomeCategoria: e.target.value } }))} style={{ ...inpStyle, flex: 1 }} />
                                  <button type='button' onClick={async () => {
                                    if (!dados._nomeCategoria) return;
                                    try {
                                      const r = await api.post('/financeiro/categorias', { nome: dados._nomeCategoria, tipo: t.tipo, cor: '#6B7A8D' });
                                      await carregarCategorias();
                                      setDadosLinha(d => ({ ...d, [i]: { ...d[i], categoria_id: String(r.data.id), _novaCategoria: false, _nomeCategoria: '' } }));
                                    } catch(e) { alert('Erro'); }
                                  }} style={{ padding: '0 10px', background: '#1A6DFF', border: 'none', color: '#fff', borderRadius: '8px', cursor: 'pointer', fontSize: '12px', fontFamily: "'DM Sans',system-ui,sans-serif", fontWeight: '600', whiteSpace: 'nowrap' }}>✓</button>
                                  <button type='button' onClick={() => setDadosLinha(d => ({ ...d, [i]: { ...d[i], _novaCategoria: false } }))} style={{ padding: '0 8px', background: '#F4F6F9', border: 'none', color: '#6B7A8D', borderRadius: '6px', cursor: 'pointer', fontSize: '12px' }}>×</button>
                                </div>
                              ) : (
                                <select value={dados.categoria_id || ''} onChange={e => setDadosLinha(d => ({ ...d, [i]: { ...d[i], categoria_id: e.target.value } }))} style={inpStyle}>
                                  <option value=''>Selecione</option>
                                  {categorias.filter(cat => cat.tipo === t.tipo || cat.tipo === 'ambos').map(cat => (
                                    <option key={cat.id} value={cat.id}>{cat.nome}</option>
                                  ))}
                                </select>
                              )}
                            </div>
                            <div>
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '5px' }}>
                                <label style={{ ...lbl, marginBottom: 0 }}>Fornecedor</label>
                                <button type='button' onClick={() => setDadosLinha(d => ({ ...d, [i]: { ...d[i], _novoForn: !d[i]?._novoForn, _nomeForn: '' } }))}
                                  style={{ fontSize: '11px', color: '#1E5FA5', background: 'none', border: 'none', cursor: 'pointer', fontFamily: "'DM Sans',system-ui,sans-serif", fontWeight: '600' }}>+ Novo</button>
                              </div>
                              {dados._novoForn ? (
                                <div style={{ display: 'flex', gap: '4px' }}>
                                  <input placeholder='Nome...' value={dados._nomeForn || ''} onChange={e => setDadosLinha(d => ({ ...d, [i]: { ...d[i], _nomeForn: e.target.value } }))} style={{ ...inpStyle, flex: 1 }} />
                                  <button type='button' onClick={async () => {
                                    if (!dados._nomeForn) return;
                                    try {
                                      const r = await api.post('/fornecedores', { nome: dados._nomeForn });
                                      await api.get('/fornecedores').then(res => setFornecedores(res.data));
                                      setDadosLinha(d => ({ ...d, [i]: { ...d[i], fornecedor_id: String(r.data.id), _novoForn: false, _nomeForn: '' } }));
                                    } catch(e) { alert('Erro'); }
                                  }} style={{ padding: '0 10px', background: '#1A6DFF', border: 'none', color: '#fff', borderRadius: '8px', cursor: 'pointer', fontSize: '12px', fontFamily: "'DM Sans',system-ui,sans-serif", fontWeight: '600', whiteSpace: 'nowrap' }}>Criar</button>
                                  <button type='button' onClick={() => setDadosLinha(d => ({ ...d, [i]: { ...d[i], _novoForn: false } }))} style={{ padding: '0 8px', background: '#F4F6F9', border: 'none', color: '#6B7A8D', borderRadius: '6px', cursor: 'pointer', fontSize: '12px' }}>x</button>
                                </div>
                              ) : (
                                <select value={dados.fornecedor_id || ''} onChange={e => setDadosLinha(d => ({ ...d, [i]: { ...d[i], fornecedor_id: e.target.value } }))} style={inpStyle}>
                                  <option value=''>Nenhum</option>
                                  {fornecedores.map(f => <option key={f.id} value={f.id}>{f.nome}</option>)}
                                </select>
                              )}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}

      {/* MODAL LANÇAMENTO */}
      {modalLanc && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,25,40,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: '#fff', borderRadius: '12px', padding: '28px', width: '580px', maxHeight: '90vh', overflowY: 'auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h3 style={{ fontSize: '16px', fontWeight: '700', color: '#1C2533', margin: 0 }}>{form.id ? 'Editar' : 'Novo'} Lançamento</h3>
              <button onClick={() => { setModalLanc(false); setForm({}); }} style={{ background: 'none', border: 'none', fontSize: '20px', color: '#9AA3AE', cursor: 'pointer' }}>×</button>
            </div>
            <form onSubmit={salvarLancamento}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 16px' }}>
                {inp('Tipo', 'tipo', 'text', [{ v: 'despesa', l: 'Despesa' }, { v: 'receita', l: 'Receita' }])}
                <div style={{ marginBottom: '12px' }}>
                  <label style={lbl}>Data vencimento</label>
                  <input type='date' value={form.data_vencimento || ''} onChange={e => {
                    const v = e.target.value;
                    // Novo lançamento: copia o vencimento para a data de pagamento, até o usuário editá-la manualmente
                    setForm(f => ({ ...f, data_vencimento: v, ...(!f.id && !f._pagManual ? { data_pagamento: v } : {}) }));
                  }} style={inpStyle} />
                </div>
              </div>
              <div style={{ marginBottom: '12px' }}>
                <label style={lbl}>Descrição</label>
                <input ref={descRef} type='text' value={form.descricao || ''} onChange={e => setV('descricao', e.target.value)} style={inpStyle} />
              </div>
              <div style={{ marginBottom: '12px' }}>
                <label style={lbl}>Tipo de lançamento</label>
                <div style={{ display: 'flex', gap: '6px' }}>
                  {[{ v: 'simples', l: 'Simples' }, { v: 'parcelado', l: 'Parcelado' }, { v: 'fixo', l: 'Fixo recorrente' }].map(opt => (
                    <button key={opt.v} type='button' onClick={() => setV('modo_lancamento', opt.v)}
                      style={{ flex: 1, padding: '7px 4px', background: modo === opt.v ? '#1A3A5C' : '#F4F6F9', border: 'none', color: modo === opt.v ? '#fff' : '#6B7A8D', borderRadius: '7px', cursor: 'pointer', fontSize: '12px', fontFamily: "'DM Sans',system-ui,sans-serif", fontWeight: modo === opt.v ? '700' : '400' }}>
                      {opt.l}
                    </button>
                  ))}
                </div>
              </div>
              {modo === 'simples' && (
                <div style={{ marginBottom: '12px' }}><label style={lbl}>Valor</label><input type='number' step='0.01' value={form.valor || ''} onChange={e => setV('valor', e.target.value)} style={inpStyle} /></div>
              )}
              {modo === 'parcelado' && (
                <div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 16px' }}>
                    <div style={{ marginBottom: '12px' }}><label style={lbl}>Valor total</label><input type='number' step='0.01' value={form.valor || ''} onChange={e => setV('valor', e.target.value)} style={inpStyle} /></div>
                    <div style={{ marginBottom: '12px' }}><label style={lbl}>Nº parcelas</label><input type='number' min='2' value={form.total_parcelas || ''} onChange={e => setV('total_parcelas', e.target.value)} style={inpStyle} /></div>
                  </div>
                  {numParcelas > 1 && parseFloat(form.valor) > 0 && (
                    <div style={{ background: '#E6F1FB', borderRadius: '8px', padding: '10px 12px', marginBottom: '12px', fontSize: '13px', color: '#1E5FA5' }}>
                      💡 {numParcelas}× de <strong>{fmt(parseFloat(form.valor || 0) / numParcelas)}</strong> · Total: {fmt(form.valor)}
                    </div>
                  )}
                </div>
              )}
              {modo === 'fixo' && (
                <div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 16px' }}>
                    <div style={{ marginBottom: '12px' }}><label style={lbl}>Valor por parcela</label><input type='number' step='0.01' value={form.valor_parcela || ''} onChange={e => setV('valor_parcela', e.target.value)} style={inpStyle} /></div>
                    <div style={{ marginBottom: '12px' }}><label style={lbl}>Nº parcelas</label><input type='number' min='1' value={form.total_parcelas || ''} onChange={e => setV('total_parcelas', e.target.value)} style={inpStyle} /></div>
                  </div>
                  {numParcelas >= 1 && valorParcela > 0 && (
                    <div style={{ background: '#EAF5EE', borderRadius: '8px', padding: '10px 12px', marginBottom: '12px', fontSize: '13px', color: '#2D7D4F' }}>
                      💡 {numParcelas}× de <strong>{fmt(valorParcela)}</strong> · Total: <strong>{fmt(valorParcela * numParcelas)}</strong>
                    </div>
                  )}
                </div>
              )}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 16px' }}>
                {inp('Conta bancária', 'conta_id', 'text', contas.map(c => ({ v: String(c.id), l: c.nome })))}
                <div style={{ marginBottom: '12px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '5px' }}>
                    <label style={{ ...lbl, marginBottom: 0 }}>Categoria</label>
                    <button type='button' onClick={() => setNovaCategoria(n => ({ ...n, show: !n.show, tipo: form.tipo || 'despesa' }))}
                      style={{ fontSize: '11px', color: '#1E5FA5', background: 'none', border: 'none', cursor: 'pointer', fontFamily: "'DM Sans',system-ui,sans-serif", fontWeight: '600' }}>+ Nova</button>
                  </div>
                  {novaCategoria.show ? (
                    <div style={{ background: '#F4F6F9', borderRadius: '8px', padding: '10px' }}>
                      <input placeholder='Nome da categoria' value={novaCategoria.nome} onChange={e => setNovaCategoria(n => ({ ...n, nome: e.target.value }))} style={{ ...inpStyle, marginBottom: '6px' }} />
                      <div style={{ display: 'flex', gap: '6px' }}>
                        <select value={novaCategoria.tipo} onChange={e => setNovaCategoria(n => ({ ...n, tipo: e.target.value }))} style={{ ...inpStyle, flex: 1 }}>
                          <option value='despesa'>Despesa</option><option value='receita'>Receita</option><option value='ambos'>Ambos</option>
                        </select>
                        <input type='color' value={novaCategoria.cor} onChange={e => setNovaCategoria(n => ({ ...n, cor: e.target.value }))} style={{ width: '36px', height: '36px', border: 'none', borderRadius: '6px', cursor: 'pointer', padding: '2px' }} />
                        <button type='button' onClick={salvarNovaCategoria} style={{ padding: '6px 10px', background: '#1A6DFF', border: 'none', color: '#fff', borderRadius: '8px', cursor: 'pointer', fontSize: '12px', fontFamily: "'DM Sans',system-ui,sans-serif", fontWeight: '600' }}>Criar</button>
                        <button type='button' onClick={() => setNovaCategoria(n => ({ ...n, show: false }))} style={{ padding: '6px 10px', background: 'transparent', border: '1px solid rgba(26,58,92,0.2)', color: '#6B7A8D', borderRadius: '6px', cursor: 'pointer', fontSize: '12px', fontFamily: "'DM Sans',system-ui,sans-serif" }}>×</button>
                      </div>
                    </div>
                  ) : (
                    <select value={form.categoria_id || ''} onChange={e => setV('categoria_id', e.target.value)} style={inpStyle}>
                      <option value=''>Selecione</option>
                      {categorias.filter(cat => !form.tipo || cat.tipo === form.tipo || cat.tipo === 'ambos').map(cat => (
                        <option key={cat.id} value={String(cat.id)}>{cat.nome}</option>
                      ))}
                    </select>
                  )}
                </div>
              </div>
              {/* Fornecedor com opcao de criar novo */}
              <div style={{ marginBottom: '12px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '5px' }}>
                  <label style={{ ...lbl, marginBottom: 0 }}>Fornecedor / Credor</label>
                  <button type='button' onClick={() => setNovoFornecedor(n => ({ ...n, show: !n.show, nome: '', cpf_cnpj: '' }))}
                    style={{ fontSize: '11px', color: '#1E5FA5', background: 'none', border: 'none', cursor: 'pointer', fontFamily: "'DM Sans',system-ui,sans-serif", fontWeight: '600' }}>+ Novo</button>
                </div>
                {novoFornecedor.show ? (
                  <div style={{ background: '#F4F6F9', borderRadius: '8px', padding: '10px' }}>
                    <input placeholder='Nome do fornecedor' value={novoFornecedor.nome} onChange={e => setNovoFornecedor(n => ({ ...n, nome: e.target.value }))} style={{ ...inpStyle, marginBottom: '6px' }} />
                    <input placeholder='CPF/CNPJ (opcional)' value={novoFornecedor.cpf_cnpj} onChange={e => setNovoFornecedor(n => ({ ...n, cpf_cnpj: e.target.value }))} style={{ ...inpStyle, marginBottom: '6px' }} />
                    <div style={{ display: 'flex', gap: '6px' }}>
                      <button type='button' onClick={salvarNovoFornecedor} style={{ padding: '6px 10px', background: '#1A6DFF', border: 'none', color: '#fff', borderRadius: '8px', cursor: 'pointer', fontSize: '12px', fontFamily: "'DM Sans',system-ui,sans-serif", fontWeight: '600' }}>Criar</button>
                      <button type='button' onClick={() => setNovoFornecedor(n => ({ ...n, show: false }))} style={{ padding: '6px 10px', background: 'transparent', border: '1px solid rgba(26,58,92,0.2)', color: '#6B7A8D', borderRadius: '6px', cursor: 'pointer', fontSize: '12px', fontFamily: "'DM Sans',system-ui,sans-serif" }}>x</button>
                    </div>
                  </div>
                ) : (
                  <select value={form.fornecedor_id || ''} onChange={e => setV('fornecedor_id', e.target.value)} style={inpStyle}>
                    <option value=''>Selecione</option>
                    {fornecedores.map(f => <option key={f.id} value={String(f.id)}>{f.nome}{f.cpf_cnpj ? ' — ' + f.cpf_cnpj : ''}</option>)}
                  </select>
                )}
              </div>
              <div style={{ marginBottom: '12px' }}>
                <label style={lbl}>Status</label>
                <select value={form.status || ''} onChange={e => {
                  const v = e.target.value;
                  // Ao marcar como "Pago", pré-preenche a data de pagamento com hoje (só se ainda vazia, respeitando edição manual)
                  setForm(f => ({ ...f, status: v, ...(v === 'pago' && !f.data_pagamento ? { data_pagamento: hoje } : {}) }));
                }} style={inpStyle}>
                  <option value=''>Selecione</option>
                  <option value='pendente'>Pendente</option>
                  <option value='pago'>Pago</option>
                </select>
              </div>
              {(form.status === 'pago' || form.id) && (
                <div style={{ marginBottom: '12px' }}>
                  <label style={lbl}>Data pagamento</label>
                  <input type='date' value={form.data_pagamento || ''} onChange={e => { const v = e.target.value; setForm(f => ({ ...f, data_pagamento: v, _pagManual: true })); }} style={inpStyle} />
                </div>
              )}
              {inp('Observações', 'observacoes')}
              <div style={{ display: 'flex', gap: '10px', marginTop: '8px' }}>
                <button type='button' onClick={() => { setModalLanc(false); setForm({}); }} style={{ flex: 1, padding: '10px', background: 'transparent', border: '1px solid rgba(26,58,92,0.2)', color: '#6B7A8D', borderRadius: '8px', cursor: 'pointer', fontFamily: "'DM Sans',system-ui,sans-serif" }}>Cancelar</button>
                <button type='button' disabled={loading} onClick={() => salvarLancamento(null, true)} style={{ flex: 1, padding: '10px', background: '#fff', border: '1px solid #1A3A5C', color: '#1A3A5C', borderRadius: '8px', cursor: 'pointer', fontWeight: '700', fontFamily: "'DM Sans',system-ui,sans-serif" }}>{loading ? '...' : 'Salvar e continuar'}</button>
                <button type='submit' disabled={loading} style={{ flex: 1, padding: '10px', background: '#1A6DFF', border: 'none', color: '#fff', borderRadius: '8px', cursor: 'pointer', fontWeight: '600', fontFamily: "'DM Sans',system-ui,sans-serif" }}>{loading ? 'Salvando...' : 'Salvar'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* MODAL PAGAR EM LOTE (painel de Fornecedores) */}
      {modalPagarLote && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,25,40,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: '#fff', borderRadius: '12px', padding: '28px', width: '380px' }}>
            <h3 style={{ fontSize: '16px', fontWeight: '700', color: '#1C2533', marginBottom: '6px' }}>Marcar como pago</h3>
            <div style={{ fontSize: '13px', color: '#6B7A8D', marginBottom: '20px' }}>
              {selLanc.length} conta(s) · {fmt((fornDetalhe?.proximas || []).filter(l => selLanc.includes(l.id)).reduce((s, l) => s + parseFloat(l.em_aberto ?? l.valor ?? 0), 0))}
            </div>
            <div style={{ marginBottom: '12px' }}><label style={lbl}>Data do pagamento</label><input type='date' value={pagLote.data_pagamento} onChange={e => setPagLote(p => ({ ...p, data_pagamento: e.target.value }))} style={inpStyle} /></div>
            <div style={{ marginBottom: '20px' }}>
              <label style={lbl}>Conta bancária</label>
              <select value={pagLote.conta_id} onChange={e => setPagLote(p => ({ ...p, conta_id: e.target.value }))} style={inpStyle}>
                <option value=''>Manter a conta de cada lançamento</option>
                {contas.map(c => <option key={c.id} value={c.id}>{c.nome}</option>)}
              </select>
            </div>
            <div style={{ display: 'flex', gap: '10px' }}>
              <button onClick={() => setModalPagarLote(false)} style={{ flex: 1, padding: '10px', background: 'transparent', border: '1px solid rgba(26,58,92,0.2)', color: '#6B7A8D', borderRadius: '8px', cursor: 'pointer', fontFamily: "'DM Sans',system-ui,sans-serif" }}>Cancelar</button>
              <button onClick={pagarLoteFornecedor} style={{ flex: 1, padding: '10px', background: '#2D7D4F', border: 'none', color: '#fff', borderRadius: '8px', cursor: 'pointer', fontWeight: '700', fontFamily: "'DM Sans',system-ui,sans-serif" }}>Confirmar</button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL PAGAR */}
      {modalPagar && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,25,40,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: '#fff', borderRadius: '12px', padding: '28px', width: '380px' }}>
            <h3 style={{ fontSize: '16px', fontWeight: '700', color: '#1C2533', marginBottom: '6px' }}>{modalPagar.tipo === 'receita' ? 'Confirmar recebimento' : 'Confirmar pagamento'}</h3>
            <div style={{ fontSize: '13px', color: '#6B7A8D', marginBottom: '20px' }}>{modalPagar.descricao} · Em aberto {fmt(modalPagar.em_aberto ?? modalPagar.valor)}</div>
            <div style={{ marginBottom: '12px' }}>
              <label style={lbl}>Valor</label>
              <input type='number' step='0.01' min='0' value={form.valor_pag ?? ''} onChange={e => setForm(f => ({ ...f, valor_pag: e.target.value }))} style={inpStyle} autoFocus />
              {parseFloat(form.valor_pag) > 0 && parseFloat(form.valor_pag) < parseFloat(modalPagar.em_aberto ?? modalPagar.valor) - 0.005 && (
                <div style={{ fontSize: '12px', color: '#E67E22', marginTop: '4px' }}>Pagamento parcial — ficará {fmt(parseFloat(modalPagar.em_aberto ?? modalPagar.valor) - parseFloat(form.valor_pag))} em aberto.</div>
              )}
              {parseFloat(form.valor_pag) > parseFloat(modalPagar.em_aberto ?? modalPagar.valor) + 0.005 && (
                <div style={{ fontSize: '12px', color: '#C0392B', marginTop: '4px' }}>Valor maior que o em aberto ({fmt(modalPagar.em_aberto ?? modalPagar.valor)})</div>
              )}
            </div>
            <div style={{ marginBottom: '12px' }}><label style={lbl}>Data do pagamento</label><input type='date' value={form.data_pagamento || hoje} onChange={e => setForm(f => ({ ...f, data_pagamento: e.target.value }))} style={inpStyle} /></div>
            <div style={{ marginBottom: '20px' }}>
              <label style={lbl}>Conta bancária</label>
              <select value={form.conta_id_pag ?? ''} onChange={e => setForm(f => ({ ...f, conta_id_pag: e.target.value }))} style={inpStyle}>
                <option value=''>Sem conta</option>
                {contas.map(c => <option key={c.id} value={c.id}>{c.nome}</option>)}
              </select>
            </div>
            <div style={{ display: 'flex', gap: '10px' }}>
              <button onClick={() => { setModalPagar(null); setForm({}); }} style={{ flex: 1, padding: '10px', background: 'transparent', border: '1px solid rgba(26,58,92,0.2)', color: '#6B7A8D', borderRadius: '8px', cursor: 'pointer', fontFamily: "'DM Sans',system-ui,sans-serif" }}>Cancelar</button>
              <button onClick={pagarLancamento} style={{ flex: 1, padding: '10px', background: '#2D7D4F', border: 'none', color: '#fff', borderRadius: '8px', cursor: 'pointer', fontWeight: '700', fontFamily: "'DM Sans',system-ui,sans-serif" }}>{modalPagar.tipo === 'receita' ? 'Confirmar recebimento' : 'Confirmar pagamento'}</button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL CONTA */}
      {modalConta && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,25,40,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: '#fff', borderRadius: '12px', padding: '28px', width: '440px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h3 style={{ fontSize: '16px', fontWeight: '700', color: '#1C2533', margin: 0 }}>{formConta.id ? 'Editar' : 'Nova'} Conta</h3>
              <button onClick={() => { setModalConta(false); setFormConta({}); }} style={{ background: 'none', border: 'none', fontSize: '20px', color: '#9AA3AE', cursor: 'pointer' }}>×</button>
            </div>
            <form onSubmit={salvarConta}>
              {[['Nome', 'nome'], ['Banco', 'banco'], ['Agência', 'agencia'], ['Conta', 'conta']].map(([l, campo]) => (
                <div key={campo} style={{ marginBottom: '12px' }}><label style={lbl}>{l}</label><input value={formConta[campo] || ''} onChange={e => setFormConta(f => ({ ...f, [campo]: e.target.value }))} style={inpStyle} /></div>
              ))}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 12px' }}>
                <div style={{ marginBottom: '12px' }}><label style={lbl}>Saldo inicial</label><input type='number' step='0.01' value={formConta.saldo_inicial || ''} onChange={e => setFormConta(f => ({ ...f, saldo_inicial: e.target.value }))} style={inpStyle} /></div>
                <div style={{ marginBottom: '12px' }}><label style={lbl}>Data de referência</label><input type='date' value={formConta.data_saldo_inicial || ''} onChange={e => setFormConta(f => ({ ...f, data_saldo_inicial: e.target.value }))} style={inpStyle} /></div>
              </div>
              <div style={{ display: 'flex', gap: '10px', marginTop: '8px' }}>
                <button type='button' onClick={() => { setModalConta(false); setFormConta({}); }} style={{ flex: 1, padding: '10px', background: 'transparent', border: '1px solid rgba(26,58,92,0.2)', color: '#6B7A8D', borderRadius: '8px', cursor: 'pointer', fontFamily: "'DM Sans',system-ui,sans-serif" }}>Cancelar</button>
                <button type='submit' style={{ flex: 1, padding: '10px', background: '#1A6DFF', border: 'none', color: '#fff', borderRadius: '8px', cursor: 'pointer', fontWeight: '600', fontFamily: "'DM Sans',system-ui,sans-serif" }}>Salvar</button>
              </div>
            </form>
          </div>
        </div>
      )}
      {/* MODAL TRANSFERÊNCIA ENTRE CONTAS */}
      {modalTransf && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,25,40,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: '#fff', borderRadius: '12px', padding: '28px', width: '460px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h3 style={{ fontSize: '16px', fontWeight: '700', color: '#1C2533', margin: 0 }}>⇄ Transferência entre contas</h3>
              <button onClick={() => setModalTransf(false)} style={{ background: 'none', border: 'none', fontSize: '20px', color: '#9AA3AE', cursor: 'pointer' }}>×</button>
            </div>
            <div style={{ marginBottom: '12px' }}>
              <label style={lbl}>Descrição</label>
              <input value={formTransf.descricao} onChange={e => setFormTransf(f => ({ ...f, descricao: e.target.value }))} style={inpStyle} />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 12px' }}>
              <div style={{ marginBottom: '12px' }}>
                <label style={lbl}>Conta origem (saída)</label>
                <select value={formTransf.conta_origem} onChange={e => setFormTransf(f => ({ ...f, conta_origem: e.target.value }))} style={inpStyle}>
                  <option value=''>Selecione</option>
                  {contas.map(ct => <option key={ct.id} value={String(ct.id)}>{ct.nome}</option>)}
                </select>
              </div>
              <div style={{ marginBottom: '12px' }}>
                <label style={lbl}>Conta destino (entrada)</label>
                <select value={formTransf.conta_destino} onChange={e => setFormTransf(f => ({ ...f, conta_destino: e.target.value }))} style={inpStyle}>
                  <option value=''>Selecione</option>
                  {contas.filter(ct => String(ct.id) !== formTransf.conta_origem).map(ct => <option key={ct.id} value={String(ct.id)}>{ct.nome}</option>)}
                </select>
              </div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 12px' }}>
              <div style={{ marginBottom: '16px' }}>
                <label style={lbl}>Valor</label>
                <input type='number' step='0.01' value={formTransf.valor} onChange={e => setFormTransf(f => ({ ...f, valor: e.target.value }))} style={inpStyle} />
              </div>
              <div style={{ marginBottom: '16px' }}>
                <label style={lbl}>Data</label>
                <input type='date' value={formTransf.data} onChange={e => setFormTransf(f => ({ ...f, data: e.target.value }))} style={inpStyle} />
              </div>
            </div>
            {formTransf.conta_origem && formTransf.conta_destino && formTransf.valor && (
              <div style={{ background: '#E6F1FB', borderRadius: '8px', padding: '10px 14px', marginBottom: '16px', fontSize: '13px', color: '#1A3A5C' }}>
                {contas.find(ct => String(ct.id) === formTransf.conta_origem)?.nome} → {contas.find(ct => String(ct.id) === formTransf.conta_destino)?.nome} · {fmt(formTransf.valor)}
              </div>
            )}
            <div style={{ display: 'flex', gap: '10px' }}>
              <button onClick={() => setModalTransf(false)}
                style={{ flex: 1, padding: '10px', background: 'transparent', border: '1px solid rgba(26,58,92,0.2)', color: '#6B7A8D', borderRadius: '8px', cursor: 'pointer', fontFamily: "'DM Sans',system-ui,sans-serif" }}>
                Cancelar
              </button>
              <button onClick={salvarTransferencia}
                style={{ flex: 1, padding: '10px', background: '#1A6DFF', border: 'none', color: '#fff', borderRadius: '8px', cursor: 'pointer', fontWeight: '600', fontFamily: "'DM Sans',system-ui,sans-serif" }}>
                ⇄ Confirmar transferência
              </button>
            </div>
          </div>
        </div>
      )}


      {modalExtrato && extrato && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(15,25,40,0.7)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 }}>
          <div style={{ background: "#fff", borderRadius: "12px", width: "860px", maxHeight: "90vh", display: "flex", flexDirection: "column" }}>
            <style>{`@keyframes spinExtrato { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
            <div style={{ padding: "18px 24px", borderBottom: "1px solid rgba(26,58,92,0.1)", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div>
                <div style={{ fontSize: "16px", fontWeight: "700", color: "#1C2533" }}>Extrato — {modalExtrato.nome}</div>
                <div style={{ fontSize: "12px", color: "#6B7A8D" }}>{modalExtrato.banco}{modalExtrato.agencia ? " · Ag: " + modalExtrato.agencia : ""}{modalExtrato.conta ? " · CC: " + modalExtrato.conta : ""}</div>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: "14px" }}>
                <button onClick={() => carregarExtrato(modalExtrato)} disabled={refreshingExtrato} title="Atualizar"
                  style={{ background: "none", border: "none", fontSize: "20px", color: "#1A3A5C", cursor: refreshingExtrato ? "default" : "pointer", display: "flex", alignItems: "center", padding: 0, lineHeight: 1 }}>
                  <span style={{ display: "inline-block", animation: refreshingExtrato ? "spinExtrato 0.7s linear infinite" : "none" }}>↻</span>
                </button>
                <button onClick={() => { setModalExtrato(null); setExtrato(null); }} style={{ background: "none", border: "none", fontSize: "22px", color: "#9AA3AE", cursor: "pointer" }}>x</button>
              </div>
            </div>
            <div style={{ padding: "12px 24px", borderBottom: "1px solid rgba(26,58,92,0.08)", display: "flex", gap: "10px", alignItems: "center", flexWrap: "wrap" }}>
              <input type="date" value={filtroExtrato.inicio} onChange={e => { const f = { ...filtroExtrato, inicio: e.target.value }; setFiltroExtrato(f); carregarExtrato(modalExtrato, f); }} style={{ padding: "6px 10px", background: "#F4F6F9", border: "0.5px solid rgba(26,58,92,0.15)", borderRadius: "7px", fontSize: "13px" }} />
              <input type="date" value={filtroExtrato.fim} onChange={e => { const f = { ...filtroExtrato, fim: e.target.value }; setFiltroExtrato(f); carregarExtrato(modalExtrato, f); }} style={{ padding: "6px 10px", background: "#F4F6F9", border: "0.5px solid rgba(26,58,92,0.15)", borderRadius: "7px", fontSize: "13px" }} />
              <div style={{ marginLeft: "auto", display: "flex", gap: "20px" }}>
                <div style={{ textAlign: "right" }}>
                  <div style={{ fontSize: "10px", color: "#9AA3AE", textTransform: "uppercase", fontWeight: "600" }}>Saldo inicial</div>
                  <div style={{ fontSize: "14px", fontWeight: "700", fontFamily: "DM Mono,monospace", color: "#6B7A8D" }}>R$ {parseFloat(extrato.saldo_inicial).toLocaleString("pt-BR", {minimumFractionDigits:2})}</div>
                </div>
                <div style={{ textAlign: "right" }}>
                  <div style={{ fontSize: "10px", color: "#9AA3AE", textTransform: "uppercase", fontWeight: "600" }}>Saldo atual</div>
                  <div style={{ fontSize: "14px", fontWeight: "700", fontFamily: "DM Mono,monospace", color: parseFloat(extrato.saldo_atual) >= 0 ? "#2D7D4F" : "#C0392B" }}>R$ {parseFloat(extrato.saldo_atual).toLocaleString("pt-BR", {minimumFractionDigits:2})}</div>
                </div>
              </div>
            </div>
            <div style={{ overflowY: "auto", flex: 1, opacity: refreshingExtrato ? 0.45 : 1, transition: "opacity 0.15s", pointerEvents: refreshingExtrato ? "none" : "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead style={{ position: "sticky", top: 0, background: "#F5F7FA" }}>
                  <tr>
                    {["Data", "Descricao", "Tipo", "Valor", "Saldo", ""].map(h => (
                      <th key={h} style={{ padding: "10px 16px", textAlign: h === "Valor" || h === "Saldo" ? "right" : "left", fontSize: "11px", color: "#9AA3AE", fontWeight: "600", textTransform: "uppercase", letterSpacing: "0.4px", borderBottom: "0.5px solid rgba(26,58,92,0.1)" }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {extrato.extrato.length === 0 && (
                    <tr><td colSpan={5} style={{ padding: "32px", textAlign: "center", color: "#9AA3AE" }}>Nenhuma movimentacao no periodo</td></tr>
                  )}
                  {extrato.extrato.map((l, idx) => (
                    <tr key={l.id} style={{ background: "#fff" }}>
                      <td style={{ padding: "12px 16px", borderBottom: "0.5px solid rgba(26,58,92,0.07)", fontSize: "12px", color: "#6B7A8D", fontFamily: "DM Mono,monospace", whiteSpace: "nowrap" }}>{l.data_pagamento ? l.data_pagamento.split("T")[0].split("-").reverse().join("/") : "—"}</td>
                      <td style={{ padding: "12px 16px", borderBottom: "0.5px solid rgba(26,58,92,0.07)", fontSize: "13px", color: "#1C2533" }}>{l.descricao}</td>
                      <td style={{ padding: "12px 16px", borderBottom: "0.5px solid rgba(26,58,92,0.07)" }}>
                        <span style={{ background: l.tipo === "receita" ? "#EAF5EE" : "#FDECEA", color: l.tipo === "receita" ? "#2D7D4F" : "#C0392B", padding: "2px 8px", borderRadius: "20px", fontSize: "11px", fontWeight: "600" }}>
                          {l.tipo === "receita" ? "Entrada" : "Saida"}
                        </span>
                      </td>
                      <td style={{ padding: "12px 16px", borderBottom: "0.5px solid rgba(26,58,92,0.07)", textAlign: "right", fontFamily: "DM Mono,monospace", fontSize: "13px", fontWeight: "700", color: l.tipo === "receita" ? "#2D7D4F" : "#C0392B" }}>
                        {l.tipo === "receita" ? "+" : "-"}R$ {parseFloat(l.valor).toLocaleString("pt-BR", {minimumFractionDigits:2})}
                      </td>
                      <td style={{ padding: "12px 16px", borderBottom: "0.5px solid rgba(26,58,92,0.07)", textAlign: "right", fontFamily: "DM Mono,monospace", fontSize: "13px", fontWeight: "700", color: parseFloat(l.saldo_apos) >= 0 ? "#1C2533" : "#C0392B" }}>
                        R$ {parseFloat(l.saldo_apos).toLocaleString("pt-BR", {minimumFractionDigits:2})}
                      </td>
                      <td style={{ padding: "12px 16px", borderBottom: "0.5px solid rgba(26,58,92,0.07)", textAlign: "center" }}>
                        {!somenteLeitura && <button onClick={async () => {
                          if (!window.confirm("Cancelar este lancamento?")) return;
                          await api.delete("/financeiro/lancamentos/" + l.id);
                          carregarExtrato(modalExtrato);
                        }} style={{ padding: "2px 8px", background: "#FDECEA", border: "none", color: "#C0392B", borderRadius: "5px", cursor: "pointer", fontSize: "12px", fontWeight: "700" }}>×</button>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
      {/* MODAL DIVIDIR PAGAMENTO */}
      {modalDividir && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,25,40,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: '#fff', borderRadius: '12px', padding: '28px', width: '520px', maxHeight: '90vh', overflowY: 'auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <h3 style={{ fontSize: '16px', fontWeight: '700', color: '#1C2533', margin: 0 }}>Dividir pagamento</h3>
              <button onClick={() => setModalDividir(null)} style={{ background: 'none', border: 'none', fontSize: '20px', color: '#9AA3AE', cursor: 'pointer' }}>×</button>
            </div>
            <div style={{ background: '#F4F6F9', borderRadius: '8px', padding: '10px 14px', marginBottom: '16px', fontSize: '13px', color: '#6B7A8D' }}>
              <strong style={{ color: '#1C2533' }}>{modalDividir.descricao}</strong> · {fmt(modalDividir.valor)}
            </div>
            <div style={{ fontSize: '12px', color: '#9AA3AE', marginBottom: '12px' }}>
              Defina o valor e a conta bancária de cada forma de pagamento. O lançamento original será cancelado e substituído pelos novos.
            </div>
            {divisoes.map((d, i) => (
              <div key={i} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto', gap: '8px', marginBottom: '8px', alignItems: 'flex-end' }}>
                <div>
                  {i === 0 && <label style={lbl}>Forma / Descrição</label>}
                  <input placeholder='Ex: PIX, Dinheiro...' value={d.forma}
                    onChange={e => setDivisoes(ds => ds.map((x,idx) => idx===i ? {...x, forma: e.target.value} : x))}
                    style={inpStyle} />
                </div>
                <div>
                  {i === 0 && <label style={lbl}>Valor</label>}
                  <input type='number' step='0.01' placeholder='0,00' value={d.valor}
                    onChange={e => setDivisoes(ds => ds.map((x,idx) => idx===i ? {...x, valor: e.target.value} : x))}
                    style={inpStyle} />
                </div>
                <div>
                  {i === 0 && <label style={lbl}>Conta</label>}
                  <select value={d.conta_id || ''} onChange={e => setDivisoes(ds => ds.map((x,idx) => idx===i ? {...x, conta_id: e.target.value} : x))} style={inpStyle}>
                    <option value=''>Selecione</option>
                    {contas.map(ct => <option key={ct.id} value={String(ct.id)}>{ct.nome}</option>)}
                  </select>
                </div>
                {divisoes.length > 2 && (
                  <button onClick={() => setDivisoes(ds => ds.filter((_,idx) => idx !== i))}
                    style={{ padding: '8px', background: '#FDECEA', border: 'none', color: '#C0392B', borderRadius: '6px', cursor: 'pointer' }}>×</button>
                )}
              </div>
            ))}
            <button onClick={() => setDivisoes(ds => [...ds, { forma: '', valor: '', conta_id: '' }])}
              style={{ padding: '7px 12px', background: 'transparent', border: '1px dashed rgba(26,58,92,0.3)', color: '#1A3A5C', borderRadius: '6px', cursor: 'pointer', fontSize: '12px', fontFamily: "'DM Sans',system-ui,sans-serif", width: '100%', marginBottom: '14px' }}>
              + Adicionar forma
            </button>
            {divisoes.filter(d => d.valor).length >= 2 && (
              <div style={{ background: '#EAF5EE', borderRadius: '8px', padding: '10px 14px', marginBottom: '14px', display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ fontSize: '13px', color: '#2D7D4F', fontWeight: '600' }}>Total dividido</span>
                <span style={{ fontFamily: "'DM Mono',monospace", fontWeight: '700', color: '#2D7D4F' }}>{fmt(divisoes.reduce((a,d) => a + parseFloat(d.valor||0), 0))}</span>
              </div>
            )}
            <div style={{ display: 'flex', gap: '10px' }}>
              <button onClick={() => setModalDividir(null)}
                style={{ flex: 1, padding: '10px', background: 'transparent', border: '1px solid rgba(26,58,92,0.2)', color: '#6B7A8D', borderRadius: '8px', cursor: 'pointer', fontFamily: "'DM Sans',system-ui,sans-serif" }}>
                Cancelar
              </button>
              <button onClick={salvarDivisao}
                style={{ flex: 1, padding: '10px', background: '#1A6DFF', border: 'none', color: '#fff', borderRadius: '8px', cursor: 'pointer', fontWeight: '600', fontFamily: "'DM Sans',system-ui,sans-serif" }}>
                ÷ Confirmar divisão
              </button>
            </div>
          </div>
        </div>
      )}

      {aba === 'Categorias' && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '14px' }}>
            {!somenteLeitura && <button onClick={() => { setFormCategoria({ tipo: 'despesa', cor: '#6B7A8D' }); setModalCategoria(true); }}
              style={{ padding: '9px 18px', background: '#1A6DFF', border: 'none', color: '#fff', borderRadius: '8px', cursor: 'pointer', fontWeight: '600', fontSize: '13px', fontFamily: "'DM Sans',system-ui,sans-serif" }}>
              + Nova Categoria
            </button>}
          </div>
          <div style={{ background: '#fff', border: '0.5px solid rgba(26,58,92,0.12)', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.08)', overflow: 'hidden' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  {['Cor', 'Nome', 'Tipo', ''].map(h => (
                    <th key={h} style={{ padding: '10px 14px', background: '#F5F7FA', color: '#9AA3AE', fontSize: '11px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.4px', textAlign: 'left', borderBottom: '0.5px solid rgba(26,58,92,0.1)' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {categorias.map(c => (
                  <tr key={c.id}>
                    <td style={{ padding: '12px 14px', borderBottom: '0.5px solid rgba(26,58,92,0.07)' }}>
                      <div style={{ width: '16px', height: '16px', borderRadius: '50%', background: c.cor || '#6B7A8D' }} />
                    </td>
                    <td style={{ padding: '12px 14px', fontSize: '13px', fontWeight: '500', color: '#1C2533', borderBottom: '0.5px solid rgba(26,58,92,0.07)' }}>{c.nome}</td>
                    <td style={{ padding: '12px 14px', borderBottom: '0.5px solid rgba(26,58,92,0.07)' }}>
                      <span style={{ background: c.tipo === 'receita' ? '#EAF5EE' : '#FDECEA', color: c.tipo === 'receita' ? '#2D7D4F' : '#C0392B', padding: '2px 8px', borderRadius: '20px', fontSize: '11px', fontWeight: '600' }}>
                        {c.tipo === 'receita' ? 'Receita' : 'Despesa'}
                      </span>
                    </td>
                    <td style={{ padding: '12px 14px', borderBottom: '0.5px solid rgba(26,58,92,0.07)', textAlign: 'right' }}>
                      {!somenteLeitura && <button onClick={() => { setFormCategoria(c); setModalCategoria(true); }}
                        style={{ padding: '3px 10px', background: 'transparent', border: '1px solid rgba(26,58,92,0.2)', color: '#6B7A8D', borderRadius: '5px', cursor: 'pointer', fontSize: '11px', marginRight: '6px' }}>Editar</button>}
                      {!somenteLeitura && <button onClick={async () => { if (window.confirm('Excluir categoria?')) { await api.delete('/financeiro/categorias/' + c.id); carregarCategorias(); } }}
                        style={{ padding: '3px 10px', background: 'transparent', border: '1px solid #C0392B', color: '#C0392B', borderRadius: '5px', cursor: 'pointer', fontSize: '11px' }}>Excluir</button>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {modalCategoria && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,25,40,0.7)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px' }}>
          <div style={{ background: '#fff', borderRadius: '12px', padding: '28px', width: '100%', maxWidth: '380px' }}>
            <h3 style={{ fontSize: '16px', fontWeight: '700', color: '#1C2533', marginBottom: '20px' }}>{formCategoria.id ? 'Editar Categoria' : 'Nova Categoria'}</h3>
            <div style={{ marginBottom: '12px' }}>
              <label style={lbl}>Nome</label>
              <input value={formCategoria.nome || ''} onChange={e => setFormCategoria(f => ({ ...f, nome: e.target.value }))} style={inpStyle} placeholder="Ex: Energia, Salário..." />
            </div>
            <div style={{ marginBottom: '12px' }}>
              <label style={lbl}>Tipo</label>
              <select value={formCategoria.tipo || 'despesa'} onChange={e => setFormCategoria(f => ({ ...f, tipo: e.target.value }))} style={inpStyle}>
                <option value='despesa'>Despesa</option>
                <option value='receita'>Receita</option>
              </select>
            </div>
            <div style={{ marginBottom: '20px' }}>
              <label style={lbl}>Cor</label>
              <input type='color' value={formCategoria.cor || '#6B7A8D'} onChange={e => setFormCategoria(f => ({ ...f, cor: e.target.value }))} style={{ ...inpStyle, height: '40px', padding: '4px' }} />
            </div>
            <div style={{ display: 'flex', gap: '8px' }}>
              <button onClick={() => { setModalCategoria(false); setFormCategoria({}); }}
                style={{ flex: 1, padding: '10px', background: 'transparent', border: '1px solid rgba(26,58,92,0.2)', color: '#6B7A8D', borderRadius: '8px', cursor: 'pointer', fontFamily: "'DM Sans',system-ui,sans-serif" }}>Cancelar</button>
              <button onClick={async () => {
                if (!formCategoria.nome) { alert('Informe o nome'); return; }
                if (formCategoria.id) await api.put('/financeiro/categorias/' + formCategoria.id, formCategoria);
                else await api.post('/financeiro/categorias', formCategoria);
                setModalCategoria(false); setFormCategoria({});
                carregarCategorias();
              }} style={{ flex: 1, padding: '10px', background: '#1A6DFF', border: 'none', color: '#fff', borderRadius: '8px', cursor: 'pointer', fontWeight: '600', fontFamily: "'DM Sans',system-ui,sans-serif" }}>Salvar</button>
            </div>
          </div>
        </div>
      )}

      {titulo && (
        <div onClick={() => { setTitulo(null); setTituloPag(null); }} style={{ position: 'fixed', inset: 0, background: 'rgba(15,25,40,0.7)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px' }}>
          <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: '12px', padding: '24px 28px', width: '100%', maxWidth: '620px', maxHeight: '90vh', overflowY: 'auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '14px' }}>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: '12px', color: '#6B7A8D', fontWeight: '600' }}>{titulo.fornecedor_nome || (titulo.tipo === 'receita' ? 'Recebimento' : 'Pagamento')}</div>
                <h3 style={{ fontSize: '17px', fontWeight: '700', color: '#1C2533', margin: '2px 0 6px' }}>{titulo.descricao}{titulo.parcial && seloParcial}</h3>
                <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap', fontSize: '12px', color: '#6B7A8D' }}>
                  {titulo.categoria_nome && <span style={{ background: corCategoria(titulo.categoria_nome, titulo.categoria_cor) + '1F', color: corCategoria(titulo.categoria_nome, titulo.categoria_cor), padding: '2px 9px', borderRadius: '20px', fontSize: '11px', fontWeight: '600' }}>{titulo.categoria_nome}</span>}
                  <span>Vencimento {fmtData(titulo.data_vencimento)}</span>
                </div>
              </div>
              <button onClick={() => { setTitulo(null); setTituloPag(null); }} style={{ background: 'none', border: 'none', fontSize: '22px', color: '#9AA3AE', cursor: 'pointer', lineHeight: 1 }}>×</button>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1.3fr 1fr 1fr', gap: '8px', marginBottom: '18px' }}>
              <div style={{ background: '#F4F6F9', borderRadius: '8px', padding: '8px 10px' }}>
                <div style={{ fontSize: '10px', color: '#9AA3AE', textTransform: 'uppercase', fontWeight: '600', marginBottom: '3px' }}>Valor total</div>
                {somenteLeitura ? <div style={{ fontSize: '15px', fontWeight: '700', fontFamily: "'DM Mono',monospace", color: '#1C2533' }}>{fmt(titulo.valor_total)}</div> : (
                  <div style={{ display: 'flex', gap: '6px' }}>
                    <input type='number' step='0.01' min='0' value={tituloValor} onChange={e => setTituloValor(e.target.value)}
                      onKeyDown={e => { if (e.key === 'Enter') salvarValorTotal(); }}
                      style={{ ...inpStyle, padding: '5px 8px', fontFamily: "'DM Mono',monospace", fontWeight: '700' }} />
                    {parseFloat(tituloValor) !== parseFloat(titulo.valor_total) && (
                      <button onClick={salvarValorTotal} style={{ padding: '5px 10px', background: '#1A6DFF', border: 'none', color: '#fff', borderRadius: '6px', cursor: 'pointer', fontSize: '12px', fontWeight: '600' }}>Salvar</button>
                    )}
                  </div>
                )}
              </div>
              <div style={{ background: '#F4F6F9', borderRadius: '8px', padding: '8px 10px' }}>
                <div style={{ fontSize: '10px', color: '#9AA3AE', textTransform: 'uppercase', fontWeight: '600', marginBottom: '3px' }}>Pago</div>
                <div style={{ fontSize: '15px', fontWeight: '700', fontFamily: "'DM Mono',monospace", color: '#2D7D4F' }}>{fmt(titulo.pago)}</div>
              </div>
              <div style={{ background: '#F4F6F9', borderRadius: '8px', padding: '8px 10px' }}>
                <div style={{ fontSize: '10px', color: '#9AA3AE', textTransform: 'uppercase', fontWeight: '600', marginBottom: '3px' }}>Em aberto</div>
                <div style={{ fontSize: '15px', fontWeight: '700', fontFamily: "'DM Mono',monospace", color: titulo.em_aberto > 0 ? '#B06000' : '#9AA3AE' }}>{fmt(titulo.em_aberto)}</div>
              </div>
            </div>

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid rgba(26,58,92,0.12)', marginBottom: '10px' }}>
              <div style={{ padding: '6px 2px', borderBottom: '2px solid #1A6DFF', color: '#1A3A5C', fontSize: '13px', fontWeight: '700', marginBottom: '-1px' }}>Pagamentos realizados</div>
              {!somenteLeitura && titulo.em_aberto > 0 && !tituloPag && (
                <button onClick={() => setTituloPag({ valor: String(titulo.em_aberto), data_pagamento: hoje, conta_id: titulo.conta_id ? String(titulo.conta_id) : '' })}
                  style={{ padding: '5px 12px', background: '#2D7D4F', border: 'none', color: '#fff', borderRadius: '7px', cursor: 'pointer', fontSize: '12px', fontWeight: '600', marginBottom: '6px', fontFamily: "'DM Sans',system-ui,sans-serif" }}>
                  + Registrar pagamento
                </button>
              )}
            </div>

            {tituloPag && (
              <div style={{ background: '#F4F9F6', border: '1px solid rgba(45,125,79,0.25)', borderRadius: '8px', padding: '12px', marginBottom: '12px' }}>
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'flex-end' }}>
                  <div style={{ flex: '1 1 120px' }}>
                    <label style={lbl}>Valor</label>
                    <input type='number' step='0.01' min='0' value={tituloPag.valor} onChange={e => setTituloPag(f => ({ ...f, valor: e.target.value }))} style={inpStyle} autoFocus />
                  </div>
                  <div style={{ flex: '1 1 130px' }}>
                    <label style={lbl}>Data</label>
                    <input type='date' value={tituloPag.data_pagamento} onChange={e => setTituloPag(f => ({ ...f, data_pagamento: e.target.value }))} style={inpStyle} />
                  </div>
                  <div style={{ flex: '1 1 150px' }}>
                    <label style={lbl}>Conta</label>
                    <select value={tituloPag.conta_id} onChange={e => setTituloPag(f => ({ ...f, conta_id: e.target.value }))} style={inpStyle}>
                      <option value=''>Sem conta</option>
                      {contas.map(c => <option key={c.id} value={c.id}>{c.nome}</option>)}
                    </select>
                  </div>
                </div>
                {parseFloat(tituloPag.valor) > 0 && parseFloat(tituloPag.valor) < titulo.em_aberto - 0.005 && (
                  <div style={{ fontSize: '12px', color: '#E67E22', marginTop: '6px' }}>Pagamento parcial — ficará {fmt(titulo.em_aberto - parseFloat(tituloPag.valor))} em aberto.</div>
                )}
                <div style={{ display: 'flex', gap: '8px', marginTop: '10px', justifyContent: 'flex-end' }}>
                  <button onClick={() => setTituloPag(null)} style={{ padding: '7px 14px', background: 'transparent', border: '1px solid rgba(26,58,92,0.2)', color: '#6B7A8D', borderRadius: '7px', cursor: 'pointer', fontSize: '12px', fontFamily: "'DM Sans',system-ui,sans-serif" }}>Cancelar</button>
                  <button onClick={registrarPagamentoTitulo} style={{ padding: '7px 14px', background: '#2D7D4F', border: 'none', color: '#fff', borderRadius: '7px', cursor: 'pointer', fontSize: '12px', fontWeight: '700', fontFamily: "'DM Sans',system-ui,sans-serif" }}>Confirmar pagamento</button>
                </div>
              </div>
            )}

            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead><tr>{['Conta', 'Data do pagamento', 'Valor', ''].map(h => <th key={h} style={th}>{h}</th>)}</tr></thead>
              <tbody>
                {titulo.baixas.length === 0 && <tr><td colSpan={4} style={{ ...td, textAlign: 'center', color: '#9AA3AE', padding: '20px' }}>Nenhum pagamento realizado</td></tr>}
                {titulo.baixas.map(b => (
                  <tr key={b.id}>
                    <td style={{ ...td, fontSize: '12px', color: '#6B7A8D' }}>{b.conta_nome || '—'}</td>
                    <td style={{ ...td, fontFamily: "'DM Mono',monospace", fontSize: '12px' }}>{fmtData(b.data_pagamento)}</td>
                    <td style={{ ...td, fontFamily: "'DM Mono',monospace", fontSize: '13px', fontWeight: '700', color: '#2D7D4F' }}>{fmt(b.valor)}</td>
                    <td style={{ ...td, textAlign: 'right' }}>
                      {!somenteLeitura && <button onClick={() => cancelarBaixa(b)} title='Cancelar este pagamento'
                        style={{ padding: '3px 8px', background: 'transparent', border: '1px solid rgba(192,57,43,0.35)', color: '#C0392B', borderRadius: '5px', cursor: 'pointer', fontSize: '13px' }}>🗑</button>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
      {confirmExcluirFilho && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,25,40,0.7)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px' }}>
          <div style={{ background: '#fff', borderRadius: '12px', padding: '28px', width: '100%', maxWidth: '420px' }}>
            <h3 style={{ fontSize: '16px', fontWeight: '700', color: '#1C2533', marginBottom: '6px' }}>Excluir lançamento</h3>
            <div style={{ fontSize: '13px', color: '#6B7A8D', marginBottom: '14px' }}>{confirmExcluirFilho.descricao} · {fmt(confirmExcluirFilho.em_aberto ?? confirmExcluirFilho.valor)}</div>
            <div style={{ fontSize: '14px', color: '#1C2533', lineHeight: 1.5, marginBottom: '20px' }}>
              Este pagamento faz parte de um lançamento parcelado. Ao excluir, este valor será abatido do total do lançamento original e deixará de ficar pendente. Deseja continuar?
            </div>
            <div style={{ display: 'flex', gap: '8px' }}>
              <button onClick={() => setConfirmExcluirFilho(null)}
                style={{ flex: 1, padding: '10px', background: 'transparent', border: '1px solid rgba(26,58,92,0.2)', color: '#6B7A8D', borderRadius: '8px', cursor: 'pointer', fontFamily: "'DM Sans',system-ui,sans-serif" }}>Cancelar</button>
              <button onClick={() => { const l = confirmExcluirFilho; setConfirmExcluirFilho(null); excluirLancFornecedor(l, true); }}
                style={{ flex: 1, padding: '10px', background: '#C0392B', border: 'none', color: '#fff', borderRadius: '8px', cursor: 'pointer', fontWeight: '700', fontFamily: "'DM Sans',system-ui,sans-serif" }}>Sim, excluir</button>
            </div>
          </div>
        </div>
      )}
      {modalParcial && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,25,40,0.7)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px' }}>
          <div style={{ background: '#fff', borderRadius: '12px', padding: '28px', width: '100%', maxWidth: '400px' }}>
            <h3 style={{ fontSize: '16px', fontWeight: '700', color: '#1C2533', marginBottom: '6px' }}>Baixa parcial</h3>
            <div style={{ fontSize: '13px', color: '#6B7A8D', marginBottom: '16px' }}>{modalParcial.descricao} · Total: {fmt(modalParcial.valor)}</div>
            <div style={{ marginBottom: '12px' }}>
              <label style={lbl}>Valor recebido agora</label>
              <input type='number' step='0.01' value={valorParcial} onChange={e => setValorParcial(e.target.value)}
                placeholder={'Max: ' + parseFloat(modalParcial.valor).toFixed(2)} style={inpStyle} autoFocus />
              {valorParcial && parseFloat(valorParcial) < parseFloat(modalParcial.valor) && (
                <div style={{ fontSize: '12px', color: '#E67E22', marginTop: '4px' }}>
                  Saldo restante: {fmt(parseFloat(modalParcial.valor) - parseFloat(valorParcial))} ficara pendente
                </div>
              )}
            </div>
            <div style={{ marginBottom: '12px' }}>
              <label style={lbl}>Conta</label>
              <select value={form.conta_id_pag || ''} onChange={e => setForm(f => ({ ...f, conta_id_pag: e.target.value }))} style={inpStyle}>
                <option value=''>Sem conta</option>
                {contas.map(c => <option key={c.id} value={c.id}>{c.nome}</option>)}
              </select>
            </div>
            <div style={{ marginBottom: '16px' }}>
              <label style={lbl}>Data de recebimento</label>
              <input type='date' value={form.data_pagamento || hoje} onChange={e => setForm(f => ({ ...f, data_pagamento: e.target.value }))} style={inpStyle} />
            </div>
            <div style={{ display: 'flex', gap: '8px' }}>
              <button onClick={() => { setModalParcial(null); setValorParcial(''); setForm({}); }}
                style={{ flex: 1, padding: '10px', background: 'transparent', border: '1px solid rgba(26,58,92,0.2)', color: '#6B7A8D', borderRadius: '8px', cursor: 'pointer', fontFamily: "'DM Sans',system-ui,sans-serif" }}>Cancelar</button>
              <button onClick={pagarParcial}
                style={{ flex: 1, padding: '10px', background: '#E67E22', border: 'none', color: '#fff', borderRadius: '8px', cursor: 'pointer', fontWeight: '700', fontFamily: "'DM Sans',system-ui,sans-serif" }}>Confirmar baixa</button>
            </div>
          </div>
        </div>
      )}
      {/* MODAL ALTERAR VALOR EM LOTE */}
      {modalValorLote && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,25,40,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: '#fff', borderRadius: '12px', padding: '28px', width: '380px' }}>
            <h3 style={{ fontSize: '16px', fontWeight: '700', color: '#1C2533', marginBottom: '6px' }}>Alterar valor em lote</h3>
            <div style={{ fontSize: '13px', color: '#6B7A8D', marginBottom: '20px' }}>{selLanc.length} lançamento(s) selecionado(s) passarão a ter o mesmo valor. Só lançamentos pendentes são alterados.</div>
            {(fornDetalhe?.proximas || []).some(l => selLanc.includes(l.id) && l.fracionado) && (
              <div style={{ fontSize: '12px', color: '#B06000', background: '#FFF3E0', borderRadius: '6px', padding: '8px 10px', marginTop: '-12px', marginBottom: '16px' }}>
                Para lançamentos com pagamento parcial, o novo valor substitui o valor total.
              </div>
            )}
            <div style={{ marginBottom: '20px' }}>
              <label style={lbl}>Novo valor</label>
              <input type='number' step='0.01' min='0' value={novoValorLote} onChange={e => setNovoValorLote(e.target.value)} style={inpStyle} autoFocus placeholder='0,00' />
            </div>
            <div style={{ display: 'flex', gap: '10px' }}>
              <button onClick={() => { setModalValorLote(false); setNovoValorLote(''); }} style={{ flex: 1, padding: '10px', background: 'transparent', border: '1px solid rgba(26,58,92,0.2)', color: '#6B7A8D', borderRadius: '8px', cursor: 'pointer', fontFamily: "'DM Sans',system-ui,sans-serif" }}>Cancelar</button>
              <button onClick={salvarValorLote} style={{ flex: 1, padding: '10px', background: '#1A6DFF', border: 'none', color: '#fff', borderRadius: '8px', cursor: 'pointer', fontWeight: '600', fontFamily: "'DM Sans',system-ui,sans-serif" }}>Confirmar</button>
            </div>
          </div>
        </div>
      )}
      {/* MODAL ALTERAR VENCIMENTO EM LOTE */}
      {modalVencLote && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,25,40,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: '#fff', borderRadius: '12px', padding: '28px', width: '380px' }}>
            <h3 style={{ fontSize: '16px', fontWeight: '700', color: '#1C2533', marginBottom: '6px' }}>Alterar vencimento em lote</h3>
            <div style={{ fontSize: '13px', color: '#6B7A8D', marginBottom: '20px' }}>{selLanc.length} lançamento(s) selecionado(s) terão a nova data de vencimento.</div>
            <div style={{ marginBottom: '20px' }}>
              <label style={lbl}>Nova data de vencimento</label>
              <input type='date' value={novaVencLote} onChange={e => setNovaVencLote(e.target.value)} style={inpStyle} autoFocus />
            </div>
            <div style={{ display: 'flex', gap: '10px' }}>
              <button onClick={() => setModalVencLote(false)} style={{ flex: 1, padding: '10px', background: 'transparent', border: '1px solid rgba(26,58,92,0.2)', color: '#6B7A8D', borderRadius: '8px', cursor: 'pointer', fontFamily: "'DM Sans',system-ui,sans-serif" }}>Cancelar</button>
              <button onClick={salvarVencimentoLote} style={{ flex: 1, padding: '10px', background: '#1A6DFF', border: 'none', color: '#fff', borderRadius: '8px', cursor: 'pointer', fontWeight: '600', fontFamily: "'DM Sans',system-ui,sans-serif" }}>Confirmar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
